import React, { useEffect, useMemo, useRef, useState } from "react";
import Icon from "./Icon";
import ConfirmDialog from "./ConfirmDialog";
import ShopMatchCard from "./ShopMatchCard";
import FilterChip from "./FilterChip";
import PushPrompt from "./PushPrompt";
import { getShopGoogleMapsUrl } from "./shopDisplayHelpers";
import { playMessageSound, unlockNotificationSound, isMessageSoundEnabled } from "../utils/notificationSound";
import { uploadAttachment as uploadAttachmentToStorage } from "../utils/attachmentUpload";

import { API_BASE } from "../utils/apiBase";
import { isDemoToken } from "../utils/demoSession";
const NO_INBOUND_UNREAD_MSG = "No inbound message found to mark unread";

const ATTACHMENT_ACCEPT = "image/*,video/*,application/pdf";
const MAX_IMAGE_BYTES = 25 * 1024 * 1024;
const MAX_VIDEO_BYTES = 250 * 1024 * 1024;
const MAX_DOC_BYTES = 25 * 1024 * 1024;

function attachmentKind(file) {
  const type = (file && file.type) || "";
  if (type.startsWith("image/")) return "image";
  if (type.startsWith("video/")) return "video";
  if (type === "application/pdf" || (file && file.name && file.name.toLowerCase().endsWith(".pdf"))) return "document";
  return null;
}

function maxBytesForKind(kind) {
  if (kind === "video") return MAX_VIDEO_BYTES;
  if (kind === "document") return MAX_DOC_BYTES;
  return MAX_IMAGE_BYTES;
}

function formatBytes(n) {
  const b = Number(n || 0);
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
}
const MIN_REFRESH_SPIN_MS = 500;
const REFRESH_SPIN_ANIMATION = "gc-refresh-spin 1s linear infinite";

function RestoreIcon({ className = "w-4 h-4" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M7 7v4h4"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M7 11a7 7 0 1 1 2.05 4.95"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// Round select control on each thread row, used for the bulk actions. The row itself opens the
// thread, so this MUST stop propagation - otherwise selecting would also open the conversation.
// role="checkbox" + aria-checked is the accurate semantic for "select this item in a list".
function ThreadSelectToggle({ selected, onToggle, label }) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={selected}
      aria-label={`Select conversation with ${label}`}
      onClick={(e) => {
        e.stopPropagation();
        onToggle();
      }}
      className={
        "mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full border transition " +
        (selected
          ? "border-blue-600 bg-blue-600 text-white dark:border-blue-500 dark:bg-blue-500"
          : "border-gray-300 bg-white hover:border-blue-500 dark:border-gray-500 dark:bg-gray-800 dark:hover:border-blue-400")
      }
    >
      {selected ? <i className="not-italic fi fi-rr-check text-[10px]" aria-hidden="true" /> : null}
    </button>
  );
}

// Signature of a message's attachments that captures only RENDER-affecting state: whether each
// one is processing, has a url yet, or has expired. Deliberately ignores the url *string* (the
// presigned url rotates server-side but points at the same object) so a stable attachment keeps
// its exact src and doesn't reload - while a video flipping processing→ready (url appears) does
// re-render so it swaps from the "processing" placeholder to the playable <video>.
function attachmentSig(m) {
  const atts = m && m.attachments;
  if (!Array.isArray(atts) || atts.length === 0) return "";
  return atts.map((a) => `${a.id}:${a.processing ? "p" : ""}${a.url ? "u" : ""}${a.expired ? "x" : ""}`).join(",");
}

// Merge a freshly-fetched thread into the existing message list WITHOUT replacing
// unchanged messages - so an already-rendered <video>/<img> keeps its exact src and
// doesn't reload (which would reset a playing video). Returns the same `prev` array
// reference when nothing changed so React skips the re-render entirely.
// Shown when GET /users/me fails. Named so the retry can clear exactly this banner and not
// stomp an unrelated error the user still needs to see.
const ME_LOAD_ERROR = "Could not load your account details.";

function reconcileThreadMessages(prev, next) {
  if (!Array.isArray(next)) return prev;
  if (!Array.isArray(prev) || prev.length === 0) return next;

  const prevById = new Map(prev.map((m) => [m.id, m]));
  let changed = next.length !== prev.length;

  const merged = next.map((m, i) => {
    const old = prevById.get(m.id);
    if (!old) {
      changed = true;
      return m; // new message
    }
    if (attachmentSig(old) !== attachmentSig(m)) {
      changed = true;
      return m; // an attachment changed (e.g. video finished processing) → render the new one
    }
    if (old.is_read !== m.is_read) {
      changed = true;
      return { ...old, is_read: m.is_read }; // reflect read-state change, keep media src
    }
    if (prev[i] !== old) changed = true; // order/membership shift
    return old; // unchanged → keep identity (stable attachment src)
  });

  return changed ? merged : prev;
}

function formatLastSeen(value) {
  if (!value) return "Offline";
  const then = new Date(value);
  if (Number.isNaN(then.getTime())) return "Offline";
  const secs = Math.max(0, Math.floor((Date.now() - then.getTime()) / 1000));
  if (secs < 60) return "last seen just now";
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `last seen ${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `last seen ${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `last seen ${days}d ago`;
  return `last seen ${then.toLocaleDateString()}`;
}

// Drop the redundant "Request/Inspection/Repair for " prefix - the "Request"
// badge next to it already conveys that this is a request.
function formatRequestSummary(summary) {
  return String(summary || "").replace(/^(Request|Inspection|Repair)\s+for\s+/i, "");
}

// Collapse contiguous inbox threads that came from the same bulk car request (they share a
// request_group_id and the server returns them adjacently) into one visual group, so the owner
// sees all the shops responding to a single request bundled together. Returns an ordered list of
// { type: "group", groupId, threads } and { type: "thread", thread } items. A request with only
// one thread for this user (request_group_size < 2), or a lone group member on this page, stays an
// ungrouped thread - so it degrades gracefully when a group straddles a page boundary.
export function groupThreadsForInbox(threads) {
  const list = Array.isArray(threads) ? threads : [];
  const items = [];
  let i = 0;
  while (i < list.length) {
    const t = list[i];
    const gid = t && t.request_group_id;
    const groupable = !!gid && (t.request_group_size || 1) >= 2;
    if (!groupable) {
      items.push({ type: "thread", thread: t });
      i += 1;
      continue;
    }
    const members = [t];
    let j = i + 1;
    while (j < list.length && list[j] && list[j].request_group_id === gid) {
      members.push(list[j]);
      j += 1;
    }
    items.push(members.length >= 2 ? { type: "group", groupId: gid, threads: members } : { type: "thread", thread: t });
    i = j;
  }
  return items;
}

function PresenceIndicator({ online, lastSeen, showLabel = true, className = "" }) {
  const text = online ? "Online" : formatLastSeen(lastSeen);
  return (
    <span className={`inline-flex items-center gap-1.5 ${className}`} title={text}>
      <span className={`inline-block w-2 h-2 rounded-full ${online ? "bg-green-500" : "bg-gray-300 dark:bg-gray-600"}`} aria-hidden="true" />
      {showLabel ? <span className={online ? "text-green-700 dark:text-green-300" : "text-gray-500 dark:text-gray-400"}>{text}</span> : null}
    </span>
  );
}

function UserIcon({ className = "w-5 h-5" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="8" r="3.2" stroke="currentColor" strokeWidth="1.8" />
      <path d="M5.5 19a6.5 6.5 0 0 1 13 0" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

function UserBlockedIcon({ className = "w-5 h-5" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="10.5" cy="8" r="3.2" stroke="currentColor" strokeWidth="1.8" />
      <path d="M4.5 19a6.5 6.5 0 0 1 11 0" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <circle cx="17.5" cy="16.5" r="3.5" stroke="currentColor" strokeWidth="1.8" />
      <path d="M15.5 14.5l4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

function InboxThreadStarIcon({ className = "", starred = false }) {
  if (starred) {
    return (
      <span className={`relative inline-flex items-center justify-center ${className}`.trim()} aria-hidden="true">
        <i className="fi fi-sr-star text-yellow-400 not-italic" />
        <i className="fi fi-rr-star text-yellow-600 dark:text-yellow-300 not-italic absolute inset-0 flex items-center justify-center" />
      </span>
    );
  }

  return <i className={`fi fi-rr-star not-italic ${className}`.trim()} aria-hidden="true" />;
}

/** Small transparent icon button (used in row/message actions) */
function IconButton({ title, onClick, disabled = false, className = "", children }) {
  const base =
    "inline-flex items-center justify-center w-7 h-7 rounded-xl transition " +
    "focus:outline-none focus:ring-2 focus:ring-black/20 disabled:opacity-50 disabled:cursor-not-allowed";
  return (
    <button type="button" title={title} onClick={onClick} disabled={disabled} className={`${base} ${className}`}>
      {children}
    </button>
  );
}

// Inline SVG refresh glyph (line/rounded, currentColor) used by RefreshButton. Unlike the Flaticon
// font glyph it replaced, an SVG is centered in its viewBox, so the spin animation rotates around the
// icon's true visual center with no drift. The two arcs + arrowheads are point-symmetric about
// (12,12), so the rotation stays perfectly balanced.
function RefreshIcon({ className = "w-6 h-6", style }) {
  return (
    <svg className={className} style={style} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M14.39 5.42 A7 7 0 0 1 14.39 18.58" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <path d="M16.12 16.12 L14.39 18.58 L17.29 19.36" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M9.61 18.58 A7 7 0 0 1 9.61 5.42" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <path d="M7.88 7.88 L9.61 5.42 L6.71 4.64" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function RefreshButton({ onClick, isRefreshing = false }) {
  return (
    <button
      onClick={onClick}
      disabled={isRefreshing}
      className="bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-900 dark:text-gray-100 w-10 h-10 rounded-xl inline-flex items-center justify-center"
      title={isRefreshing ? "Refreshing..." : "Refresh"}
      aria-label={isRefreshing ? "Refreshing..." : "Refresh"}
    >
      <RefreshIcon
        className="w-6 h-6"
        style={{
          transformOrigin: "center",
          // Animate only while refreshing. At rest there is no transform, so the icon sits at its
          // natural orientation instead of frozen mid-rotation (which looked off-center, and a
          // different angle on each stop). The spin snaps cleanly back to 0 when it stops.
          animation: isRefreshing ? REFRESH_SPIN_ANIMATION : undefined,
        }}
      />
    </button>
  );
}

function MessageInbox({ token, onLogout, onSessionExpired, prefEnabled = true }) {
  const [threads, setThreads] = useState([]);
  const [selectedThread, setSelectedThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState("");
  // Attachments being composed: { localId, file, kind, status: 'uploading'|'done'|'error', progress, id?, error? }
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [attachmentWarningDismissed, setAttachmentWarningDismissed] = useState(
    () => (typeof localStorage !== "undefined" && localStorage.getItem("gca.attachmentWarningDismissed") === "true")
  );
  const fileInputRef = useRef(null);
  const attachmentRetryRef = useRef(false);
  const [error, setError] = useState("");
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [loadingThreads, setLoadingThreads] = useState(true);
  const [workOrderBusy, setWorkOrderBusy] = useState(false);
  const [trashMode, setTrashMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showTrashSelectedConfirm, setShowTrashSelectedConfirm] = useState(false);
  const [showMarkAllReadConfirm, setShowMarkAllReadConfirm] = useState(false);
  const [showBlockToggleConfirm, setShowBlockToggleConfirm] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportCategory, setReportCategory] = useState("harassment");
  const [reportReason, setReportReason] = useState("");
  const [reportSubmitting, setReportSubmitting] = useState(false);
  const [reportDone, setReportDone] = useState("");
  const [showPermanentDeleteSelectedConfirm, setShowPermanentDeleteSelectedConfirm] = useState(false);
  // Single-item permanent delete (message or thread) - drives the shared ConfirmDialog
  // that replaced the old window.confirm prompts.
  const [permanentDeleteTarget, setPermanentDeleteTarget] = useState(null); // { kind: "message"|"thread", id }
  const [permanentDeleteBusy, setPermanentDeleteBusy] = useState(false);
  const [showEmptyTrashConfirm, setShowEmptyTrashConfirm] = useState(false);
  const [showStartWorkOrderConfirm, setShowStartWorkOrderConfirm] = useState(false);
  const [showCancelWorkOrderConfirm, setShowCancelWorkOrderConfirm] = useState(false);
  const [showArchiveWorkOrderConfirm, setShowArchiveWorkOrderConfirm] = useState(false);
  const [showRestoreWorkOrderConfirm, setShowRestoreWorkOrderConfirm] = useState(false);
  const [showWorkOrderStatusModal, setShowWorkOrderStatusModal] = useState(false);
  const [workOrderStatusActionType, setWorkOrderStatusActionType] = useState("");
  const [workOrderStatusChoice, setWorkOrderStatusChoice] = useState("scheduled");
  const [workOrderStatusNote, setWorkOrderStatusNote] = useState("");
  const [workOrderScheduledAt, setWorkOrderScheduledAt] = useState("");
  const WORK_ORDER_STATUS_COLORS = {
    pending: "bg-amber-100 dark:bg-gray-900 text-amber-900 dark:text-amber-200",
    accepted: "bg-blue-100 dark:bg-blue-950/40 text-blue-900 dark:text-blue-200",
    rejected: "bg-red-100 dark:bg-red-950/40 text-red-900 dark:text-red-200",
    quoted: "bg-indigo-100 dark:bg-indigo-950/40 text-indigo-900 dark:text-indigo-300",
    scheduled: "bg-cyan-100 dark:bg-cyan-950/40 text-cyan-900 dark:text-cyan-200",
    in_progress: "bg-orange-100 dark:bg-gray-900 text-orange-900 dark:text-orange-200",
    completed: "bg-emerald-100 dark:bg-emerald-950/40 text-emerald-900 dark:text-emerald-200",
    closed: "bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-100",
  };
  const WORK_ORDER_CARD_COLORS = {
    pending: "border-amber-200 dark:border-amber-700 bg-amber-50 dark:bg-gray-900 text-amber-900 dark:text-amber-200",
    accepted: "border-blue-200 dark:border-blue-900 bg-blue-50 dark:bg-blue-950/40 text-blue-900 dark:text-blue-200",
    rejected: "border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-950/40 text-red-900 dark:text-red-200",
    quoted: "border-indigo-200 dark:border-indigo-900 bg-indigo-50 dark:bg-indigo-950/40 text-indigo-900 dark:text-indigo-300",
    scheduled: "border-cyan-200 dark:border-cyan-900 bg-cyan-50 dark:bg-cyan-950/40 text-cyan-900 dark:text-cyan-200",
    in_progress: "border-orange-200 dark:border-orange-700 bg-orange-50 dark:bg-gray-900 text-orange-900 dark:text-orange-200",
    completed: "border-emerald-200 dark:border-emerald-900 bg-emerald-50 text-emerald-900 dark:text-emerald-200",
    closed: "border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-100",
  };
  const WORK_ORDER_STATUS_OPTIONS = [
    { value: "quoted", label: "Quoted", classes: "border-indigo-300 dark:border-indigo-800 bg-indigo-50 dark:bg-indigo-950/40 text-indigo-900 dark:text-indigo-300" },
    { value: "scheduled", label: "Scheduled", classes: "border-blue-300 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/40 text-blue-900 dark:text-blue-200" },
    { value: "in_progress", label: "In Progress", classes: "border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-gray-900 text-amber-900 dark:text-amber-200" },
    { value: "completed", label: "Completed", classes: "border-emerald-300 dark:border-emerald-800 bg-emerald-50 text-emerald-900 dark:text-emerald-200" },
    { value: "closed", label: "Closed", classes: "border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200" },
  ];

  const [userEmail, setUserEmail] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);
  // Bumped by the poll while currentUserId is still unknown, to re-run the /users/me effect.
  // Without this a single failed request at mount was permanent for the whole session: the id
  // never arrived, so no thread could identify its other participant.
  const [meRetryTick, setMeRetryTick] = useState(0);
  const [userTimeZone, setUserTimeZone] = useState(null);

  const [blockedUserIds, setBlockedUserIds] = useState([]);
  const [favoriteShopUserIds, setFavoriteShopUserIds] = useState([]);
  const [threadOtherUserId, setThreadOtherUserId] = useState(null);
  const [pinnedShopCard, setPinnedShopCard] = useState(null);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [hasNextPage, setHasNextPage] = useState(false);

  const [searchQuery, setSearchQuery] = useState("");
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [starredOnly, setStarredOnly] = useState(false);
  const [workOrderOnly, setWorkOrderOnly] = useState(false);
  const [archivedWorkOrderOnly, setArchivedWorkOrderOnly] = useState(false);
  const [counts, setCounts] = useState({
    unread: 0,
    work_order_active: 0,
    work_order_active_unread: 0,
    work_order_archived_unread: 0,
  });

  const pollTimerRef = useRef(null);
  // Holds the newest poll callback. The interval below reads it instead of closing over one,
  // so a section/filter change never leaves a stale fetcher running (see the poll effect).
  const pollTickRef = useRef(null);
  const bottomRef = useRef(null);
  const shouldScrollToBottomRef = useRef(false);
  // Tracks message ids already seen in the open thread, to ding once on a new inbound message.
  const seenThreadMsgIdsRef = useRef({ threadId: null, ids: new Set() });
  // Tracks total unread across the list, to ding once when it rises on a silent poll.
  const prevListUnreadRef = useRef(null);

  const transientTimerRef = useRef(null);

  useEffect(() => {
    try {
      if (token) {
        const tokenPayload = JSON.parse(atob(token.split(".")[1]));
        setUserEmail(tokenPayload.sub);
      }
    } catch {
      setUserEmail(null);
    }
  }, [token]);

  const headers = {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };

  function clearTransientTimer() {
    if (transientTimerRef.current) {
      clearTimeout(transientTimerRef.current);
      transientTimerRef.current = null;
    }
  }

  function showError(msg) {
    clearTransientTimer();
    setError(msg || "");
    if ((msg || "") === NO_INBOUND_UNREAD_MSG) {
      transientTimerRef.current = setTimeout(() => {
        setError((prev) => (prev === NO_INBOUND_UNREAD_MSG ? "" : prev));
      }, 3000);
    }
  }

  useEffect(() => {
    return () => clearTransientTimer();
  }, []);

  function parseServerTimestamp(ts) {
    if (!ts) return null;
    if (typeof ts === "string") {
      const hasTz = /[zZ]$|[+-]\d\d:\d\d$/.test(ts);
      return new Date(hasTz ? ts : ts + "Z");
    }
    return new Date(ts);
  }

  function formatTimestamp(ts) {
    const d = parseServerTimestamp(ts);
    if (!d || Number.isNaN(d.getTime())) return "";
    const tz = userTimeZone || Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
    try {
      return new Intl.DateTimeFormat(undefined, {
        timeZone: tz,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      }).format(d);
    } catch {
      return d.toLocaleString();
    }
  }

  // Day-divider label for the thread view: Today / Yesterday / a short date. Bubbles then
  // show only the time (formatTimeOnly), which keeps long conversations scannable.
  function formatDayDivider(ts) {
    const d = parseServerTimestamp(ts);
    if (!d || Number.isNaN(d.getTime())) return "";
    const tz = userTimeZone || Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
    try {
      // en-CA renders YYYY-MM-DD, giving a stable per-day key in the user's zone.
      const dayKey = (x) => new Intl.DateTimeFormat("en-CA", { timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit" }).format(x);
      const now = new Date();
      if (dayKey(d) === dayKey(now)) return "Today";
      if (dayKey(d) === dayKey(new Date(now.getTime() - 24 * 60 * 60 * 1000))) return "Yesterday";
      return new Intl.DateTimeFormat(undefined, { timeZone: tz, year: "numeric", month: "short", day: "numeric" }).format(d);
    } catch {
      return d.toLocaleDateString();
    }
  }

  function formatTimeOnly(ts) {
    const d = parseServerTimestamp(ts);
    if (!d || Number.isNaN(d.getTime())) return "";
    const tz = userTimeZone || Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
    try {
      return new Intl.DateTimeFormat(undefined, { timeZone: tz, hour: "2-digit", minute: "2-digit" }).format(d);
    } catch {
      return d.toLocaleTimeString();
    }
  }

  // Compact date + time split onto two lines, so the thread-list timestamp column
  // stays narrow (more room for the name/summary, esp. on mobile).
  function formatTimestampParts(ts) {
    const d = parseServerTimestamp(ts);
    if (!d || Number.isNaN(d.getTime())) return { date: "", time: "" };
    const tz = userTimeZone || Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
    try {
      const date = new Intl.DateTimeFormat(undefined, { timeZone: tz, year: "2-digit", month: "2-digit", day: "2-digit" }).format(d);
      const time = new Intl.DateTimeFormat(undefined, { timeZone: tz, hour: "2-digit", minute: "2-digit" }).format(d);
      return { date, time };
    } catch {
      return { date: d.toLocaleDateString(), time: "" };
    }
  }

  function withTs(url) {
    const ts = Date.now();
    return url.includes("?") ? `${url}&_ts=${ts}` : `${url}?_ts=${ts}`;
  }


  async function fetchWithAuth(url, options = {}) {
    const opts = { ...options };
    if (!opts.cache) opts.cache = "no-store";
    if (!opts.headers) opts.headers = headers;

    const res = await fetch(url, opts);
    if (res.status === 401 || res.status === 403) {
      if (onSessionExpired) onSessionExpired("/login");
      else if (onLogout) onLogout();
      throw new Error("Session expired");
    }
    return res;
  }

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const res = await fetchWithAuth(`${API_BASE}/users/me`, { headers });
        if (!res.ok) {
          // Was "Could not cancel work order." - a copy-paste that reported an unrelated action
          // and made this failure impossible to recognise. It matters more than it looks: without
          // an id here, nothing can tell which participant in a thread is the other one.
          showError(ME_LOAD_ERROR);
          return;
        }
        const me = await res.json();
        // Clear a banner left by an earlier attempt. showError only auto-clears one specific
        // transient message, so without this a single failure stayed on screen even once the
        // retry had succeeded and nothing was wrong any more.
        setError((prev) => (prev === ME_LOAD_ERROR ? "" : prev));
        setCurrentUserId(me?.id || null);
        setUserTimeZone(me?.time_zone || null);
      } catch {
        setCurrentUserId(null);
        setUserTimeZone(null);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, meRetryTick]);

  async function refreshBlockedListSilent() {
    if (!token) return;
    try {
      const res = await fetchWithAuth(withTs(`${API_BASE}/users/blocks`), { headers });
      // Silent by name and by contract - this runs on every 15s poll tick, so surfacing anything
      // here puts a permanent error banner on the inbox. It also used to claim a work order could
      // not be cancelled, which is neither what was attempted nor what failed.
      if (!res.ok) return;
      const data = await res.json();
      const ids = Array.isArray(data) ? data.map((x) => x.blocked_user_id).filter(Boolean) : [];
      setBlockedUserIds(ids);
    } catch {}
  }

  async function refreshFavoriteShopsSilent() {
    if (!token) return;
    try {
      const res = await fetchWithAuth(withTs(`${API_BASE}/users/favorite-shops`), { headers });
      if (!res.ok) return;
      const data = await res.json();
      const ids = Array.isArray(data) ? data.map((x) => x.shop_user_id).filter(Boolean) : [];
      setFavoriteShopUserIds(ids);
    } catch {}
  }

  useEffect(() => {
    refreshBlockedListSilent();
    refreshFavoriteShopsSilent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  async function fetchCounts() {
    try {
      const res = await fetchWithAuth(withTs(`${API_BASE}/messages/threads/counts`), { headers });
      if (!res.ok) return;
      const data = await res.json();
      setCounts({
        unread: Number(data?.unread || 0),
        work_order_active: Number(data?.work_order_active || 0),
        work_order_active_unread: Number(data?.work_order_active_unread || 0),
        work_order_archived_unread: Number(data?.work_order_archived_unread || 0),
      });
    } catch {}
  }

  async function loadThreads(pg = page, sz = pageSize, isTrash = trashMode, { silent = false } = {}) {
    if (!silent) {
      setSelectedThread(null);
      setMessages([]);
      setSelectedIds([]);
      setThreadOtherUserId(null);
      showError("");
    }

    try {
      // Section + filters are applied server-side so each page comes back full of rows that
      // actually belong to the section. Filtering client-side would leave pages that look
      // empty while "next page" stays enabled (the server counts unfiltered rows).
      const workOrderParam = archivedWorkOrderOnly ? "archived" : workOrderOnly ? "active" : "any";
      const baseUrl = isTrash
        ? `${API_BASE}/messages/threads/trash?page=${pg}&page_size=${sz}`
        : `${API_BASE}/messages/threads?page=${pg}&page_size=${sz}` +
          `&include_archived=${archivedWorkOrderOnly ? "true" : "false"}` +
          `&work_order=${workOrderParam}` +
          `&unread_only=${unreadOnly ? "true" : "false"}` +
          `&starred_only=${starredOnly ? "true" : "false"}`;

      const res = await fetchWithAuth(withTs(baseUrl), { headers });
      if (!res.ok) {
        if (!silent) showError("Could not load threads");
        setThreads([]);
        setHasNextPage(false);
        return;
      }

      const data = await res.json();
      const list = Array.isArray(data) ? data : [];

      // Inbox-list chime: ding once when total unread rises on a silent poll while on the
      // list (no thread open - the open-thread chime in loadThread covers that case).
      try {
        const totalUnread = list.reduce((s, t) => s + (t.unread_count || 0), 0);
        const prev = prevListUnreadRef.current;
        if (
          silent &&
          prev !== null &&
          totalUnread > prev &&
          !selectedThread &&
          isMessageSoundEnabled() &&
          document.visibilityState === "visible"
        ) {
          playMessageSound();
        }
        prevListUnreadRef.current = totalUnread;
      } catch {}

      setThreads(list);
      setHasNextPage(list.length === sz);
      fetchCounts();
    } catch {
      if (!silent) showError("Could not load threads (network error)");
      setThreads([]);
      setHasNextPage(false);
    } finally {
      // Gate the "No threads found." empty state until the first load settles, so a cold
      // open shows a loading hint instead of a false-empty inbox.
      setLoadingThreads(false);
    }
  }

  useEffect(() => {
    loadThreads(page, pageSize, trashMode, { silent: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, trashMode, page, pageSize, workOrderOnly, archivedWorkOrderOnly, unreadOnly, starredOnly]);

  async function loadThread(threadId, { silent = false, scroll = false, forceReplace = false } = {}) {
    try {
      if (!silent) showError("");

      const baseUrl = `${API_BASE}/messages/thread/${threadId}${trashMode ? "?trash=true" : ""}`;
      const res = await fetchWithAuth(withTs(baseUrl), { headers });

      if (!res.ok) {
        let detail = "";
        try {
          detail = (await res.json())?.detail;
        } catch {}
        if (!silent) showError(`Could not load thread (${threadId} ${res.status} ${detail})`);
        setMessages([]);
        return;
      }

      const data = await res.json();

      try {
        if (currentUserId && Array.isArray(data) && data.length > 0) {
          const first = data[0];
          const otherId = first.sender_id === currentUserId ? first.recipient_id : first.sender_id;
          setThreadOtherUserId(otherId || null);
        } else {
          setThreadOtherUserId(null);
        }
      } catch {
        setThreadOtherUserId(null);
      }

      if (scroll) shouldScrollToBottomRef.current = true;

      // A new inbound message arriving in the OPEN thread → play a short chime. While you're
      // actively in the app the OS/in-page notifications are suppressed (de-dup), so this is
      // the cue that someone replied in the conversation you're reading.
      try {
        const incoming = Array.isArray(data) ? data : [];
        const seen = seenThreadMsgIdsRef.current;
        const sameThread = seen.threadId === threadId;
        if (silent && !forceReplace && sameThread) {
          const hasNewInbound = incoming.some(
            (m) => !seen.ids.has(m.id) && String(m.sender_id) !== String(currentUserId)
          );
          if (hasNewInbound && isMessageSoundEnabled() && document.visibilityState === "visible") {
            playMessageSound();
          }
        }
        seenThreadMsgIdsRef.current = { threadId, ids: new Set(incoming.map((m) => m.id)) };
      } catch {}

      // Silent refreshes (the 15s poll + post-mutation reloads) merge into the
      // existing list so unchanged messages keep their media src and don't reload.
      // Explicit opens / manual refresh / error-recovery fully replace.
      if (silent && !forceReplace) {
        setMessages((prev) => reconcileThreadMessages(prev, Array.isArray(data) ? data : []));
      } else {
        setMessages(Array.isArray(data) ? data : []);
      }
      setSelectedThread(threadId);

      await loadThreads(page, pageSize, trashMode, { silent: true });
    } catch {
      if (!silent) showError(`Could not load thread (${threadId})`);
      setMessages([]);
    }
  }

  useEffect(() => {
    if (!selectedThread) return;
    if (!shouldScrollToBottomRef.current) return;
    requestAnimationFrame(() => {
      bottomRef.current?.scrollIntoView({ behavior: "auto" });
      shouldScrollToBottomRef.current = false;
    });
  }, [selectedThread, messages]);

  const selectedThreadMeta = useMemo(() => {
    if (!selectedThread) return null;
    return threads.find((x) => x.thread_id === selectedThread) || null;
  }, [threads, selectedThread]);

  const selectedThreadIsStarred = useMemo(() => {
    if (!selectedThreadMeta) return false;
    return !!selectedThreadMeta.is_starred;
  }, [selectedThreadMeta]);

  const selectedThreadUserDeleted = !!selectedThreadMeta?.opposing_user_deleted;
  const selectedThreadOtherUserIsShop = !!selectedThreadMeta?.opposing_user_is_shop;
  const selectedThreadOpposingOnline = !!selectedThreadMeta?.opposing_user_online;
  const selectedThreadOpposingLastSeen = selectedThreadMeta?.opposing_user_last_seen_at || null;
  const selectedThreadWorkOrderActive = !!selectedThreadMeta?.work_order_active;
  const selectedThreadWorkOrderStatus = selectedThreadMeta?.work_order_status || null;
  const selectedThreadWorkOrderRequestedStatus = selectedThreadMeta?.work_order_requested_status || null;
  const selectedThreadWorkOrderLastUpdatedAt = selectedThreadMeta?.work_order_updated_at || selectedThreadMeta?.timestamp || null;
  const selectedThreadWorkOrderScheduledAt = selectedThreadMeta?.work_order_scheduled_at || null;

  const isOtherUserBlocked = threadOtherUserId && blockedUserIds.includes(threadOtherUserId);
  const isOtherUserFavorited = threadOtherUserId && favoriteShopUserIds.includes(threadOtherUserId);

  // Pin the shop's card at the top of the thread for the owner's view. Only the
  // opposing user being a shop (i.e. the viewer is the customer) loads a card;
  // shops don't see a pinned card for the customer they're talking to.
  // Deliberately NOT gated on selectedThreadOtherUserIsShop being true, only on it not being
  // known to be false. That flag comes from selectedThreadMeta, which is looked up in the `threads`
  // array - so any time the thread LIST fails while the thread itself loads (a 429, a network
  // blip, or simply an open thread that is not on the current page of the list), the flag reads
  // false and the card silently disappeared from a conversation that was rendering fine.
  // threadOtherUserId comes from the thread's own messages, so it is available either way, and
  // GET /messages/shops/{id} already 404s for a non-shop - which the catch below treats as "no
  // card". Speculating costs one 404 only in the case where the list has not loaded yet.
  const otherUserKnownNotShop = !!selectedThreadMeta && !selectedThreadOtherUserIsShop;

  // Prefer the id the server names outright. threadOtherUserId is derived client-side by comparing
  // each message against currentUserId, so it is null whenever GET /users/me has not landed - and
  // that was enough to keep the card from ever being fetched. The thread list carries the id
  // directly, so between the two the card survives either request failing.
  const shopCardUserId = selectedThreadMeta?.opposing_user_id || threadOtherUserId;

  useEffect(() => {
    if (!selectedThread || !shopCardUserId || otherUserKnownNotShop) {
      setPinnedShopCard(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchWithAuth(`${API_BASE}/messages/shops/${shopCardUserId}`, { headers });
        if (cancelled) return;
        if (!res.ok) {
          // 404 is the normal "the other participant is not a shop" answer - stay quiet for it.
          // Anything else (429 from the rate limit, 5xx, auth) is a real failure that used to
          // vanish without a trace, leaving a thread that renders fine but has no card and no
          // way to tell why. One line in the console makes it diagnosable.
          if (res.status !== 404) {
            console.warn(`[shop-card] ${res.status} for shop ${shopCardUserId} - card hidden`);
          }
          setPinnedShopCard(null);
          return;
        }
        const data = await res.json();
        if (!cancelled) setPinnedShopCard(data || null);
      } catch {
        if (!cancelled) setPinnedShopCard(null);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedThread, shopCardUserId, otherUserKnownNotShop]);

  async function toggleBlockForThreadUser() {
    if (!threadOtherUserId) return;

    try {
      if (isOtherUserBlocked) {
        const res = await fetchWithAuth(`${API_BASE}/users/blocks/${threadOtherUserId}`, {
          method: "DELETE",
          headers,
        });
        if (res.ok) setBlockedUserIds((ids) => ids.filter((x) => x !== threadOtherUserId));
      } else {
        const res = await fetchWithAuth(`${API_BASE}/users/blocks`, {
          method: "POST",
          headers,
          body: JSON.stringify({ blocked_user_id: threadOtherUserId }),
        });
        if (res.ok) setBlockedUserIds((ids) => (ids.includes(threadOtherUserId) ? ids : [...ids, threadOtherUserId]));
      }
    } finally {
      setShowBlockToggleConfirm(false);
      refreshBlockedListSilent();
      refreshFavoriteShopsSilent();
    }
  }

  function openReportModal() {
    setReportCategory("harassment");
    setReportReason("");
    setReportDone("");
    setShowReportModal(true);
  }

  async function submitReport() {
    if (!selectedThread) return;
    setReportSubmitting(true);
    try {
      const res = await fetchWithAuth(`${API_BASE}/messages/report`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          category: reportCategory,
          reason: reportReason,
          thread_id: selectedThread,
          reported_user_id: threadOtherUserId || undefined,
        }),
      });
      if (res.ok) {
        const data = await res.json().catch(() => ({}));
        setReportDone(data?.duplicate ? "You already have an open report for this user." : "Report submitted. Our team will review it.");
        setReportReason("");
      } else {
        const data = await res.json().catch(() => ({}));
        setReportDone(data?.detail || "Could not submit report.");
      }
    } catch {
      setReportDone("Could not submit report (network error).");
    } finally {
      setReportSubmitting(false);
    }
  }

  async function toggleFavoriteForThreadUser() {
    if (!threadOtherUserId || !selectedThreadOtherUserIsShop) return;
    try {
      if (isOtherUserFavorited) {
        const res = await fetchWithAuth(`${API_BASE}/users/favorite-shops/${threadOtherUserId}`, {
          method: "DELETE",
          headers,
        });
        if (res.ok) setFavoriteShopUserIds((ids) => ids.filter((x) => x !== threadOtherUserId));
      } else {
        const res = await fetchWithAuth(`${API_BASE}/users/favorite-shops`, {
          method: "POST",
          headers,
          body: JSON.stringify({ shop_user_id: threadOtherUserId }),
        });
        if (res.ok) setFavoriteShopUserIds((ids) => (ids.includes(threadOtherUserId) ? ids : [...ids, threadOtherUserId]));
      }
    } catch {
      showError("Failed to update favorite shops.");
    }
  }

  // Upload one file via the shared uploader: it strips image metadata in the browser, presigns,
  // then PUTs the bytes directly to object storage. (Reused by the car-request uploader too.)
  async function uploadAttachment(item) {
    const { file, localId } = item;
    return uploadAttachmentToStorage(file, {
      token,
      apiBase: API_BASE,
      threadId: selectedThread,
      onProgress: (frac) => {
        const pct = Math.round(frac * 100);
        setSelectedFiles((prev) => prev.map((f) => (f.localId === localId ? { ...f, progress: pct } : f)));
      },
    });
  }

  async function handleFilesSelected(e) {
    const files = Array.from(e.target.files || []);
    if (fileInputRef.current) fileInputRef.current.value = ""; // allow re-picking the same file
    for (const file of files) {
      const kind = attachmentKind(file);
      if (!kind) {
        showError(`Unsupported file type: ${file.name}`);
        continue;
      }
      const limit = maxBytesForKind(kind);
      if (file.size > limit) {
        showError(`${file.name} exceeds the ${Math.round(limit / (1024 * 1024))}MB limit`);
        continue;
      }
      const localId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      setSelectedFiles((prev) => [...prev, { localId, file, kind, status: "uploading", progress: 0 }]);
      try {
        const id = await uploadAttachment({ localId, file });
        setSelectedFiles((prev) => prev.map((f) => (f.localId === localId ? { ...f, status: "done", id, progress: 100 } : f)));
      } catch (err) {
        setSelectedFiles((prev) => prev.map((f) => (f.localId === localId ? { ...f, status: "error", error: String(err.message || err) } : f)));
      }
    }
  }

  function removeSelectedFile(localId) {
    setSelectedFiles((prev) => prev.filter((f) => f.localId !== localId));
  }

  // Retry a failed upload in place, keeping the same chip (no need to remove + re-pick).
  async function retrySelectedFile(localId) {
    const entry = selectedFiles.find((f) => f.localId === localId);
    if (!entry || entry.status !== "error") return;
    setSelectedFiles((prev) => prev.map((f) => (f.localId === localId ? { ...f, status: "uploading", progress: 0, error: undefined } : f)));
    try {
      const id = await uploadAttachment({ localId, file: entry.file });
      setSelectedFiles((prev) => prev.map((f) => (f.localId === localId ? { ...f, status: "done", id, progress: 100 } : f)));
    } catch (err) {
      setSelectedFiles((prev) => prev.map((f) => (f.localId === localId ? { ...f, status: "error", error: String(err.message || err) } : f)));
    }
  }

  function dismissAttachmentWarning() {
    setAttachmentWarningDismissed(true);
    try { localStorage.setItem("gca.attachmentWarningDismissed", "true"); } catch {}
  }

  // A presigned playback URL can expire if the thread stays open a very long time;
  // on a media error, force a full (non-merge) reload to pick up regenerated URLs.
  // Guarded so it runs at most once per few seconds (avoids loops).
  function handleAttachmentError(threadId) {
    if (attachmentRetryRef.current) return;
    attachmentRetryRef.current = true;
    loadThread(threadId, { silent: true, scroll: false, forceReplace: true }).finally(() => {
      setTimeout(() => { attachmentRetryRef.current = false; }, 5000);
    });
  }

  async function sendReply() {
    if (selectedThreadUserDeleted) {
      showError("This user deleted their account. This thread is now read-only.");
      return;
    }
    if (selectedFiles.some((f) => f.status === "uploading")) {
      showError("Please wait for attachments to finish uploading.");
      return;
    }
    if (selectedFiles.some((f) => f.status === "error")) {
      // Never silently drop a failed attachment - make the user retry or remove it.
      showError("An attachment failed to upload. Retry or remove it before sending.");
      return;
    }
    const attachmentIds = selectedFiles.filter((f) => f.status === "done").map((f) => f.id);
    if (!reply.trim() && attachmentIds.length === 0) return;
    if (isSending) return;
    setIsSending(true);
    try {
      const res = await fetchWithAuth(`${API_BASE}/messages/`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          content: reply,
          zip_code: "00000",
          thread_id: selectedThread,
          attachment_ids: attachmentIds,
        }),
      });

      if (!res.ok) {
        let detail = "";
        try {
          detail = (await res.json())?.detail;
        } catch {}
        showError(`Could not send reply (${res.status} ${detail})`);
        return;
      }

      setReply("");
      setSelectedFiles([]);
      // Scroll to the message the user just sent - a chat should follow your own sends.
      shouldScrollToBottomRef.current = true;
      await loadThread(selectedThread, { silent: true, scroll: false });
    } catch {
      showError("Could not send reply (network error)");
    } finally {
      setIsSending(false);
    }
  }

  function displaySender(msg) {
    if (msg.sender_deleted) return "Deleted User";
    if (msg.sender && msg.sender.email && userEmail && msg.sender.email === userEmail) return "You";
    if (msg.sender && msg.sender.is_shop) return msg.sender.shop_name || "Shop";
    if (msg.sender) return `${msg.sender.first_name || ""} ${msg.sender.last_name || ""}`.trim();
    return msg.sender_name || msg.sender_id;
  }

  async function markMessageRead(messageId) {
    const res = await fetchWithAuth(`${API_BASE}/messages/message/${messageId}/mark_read`, { method: "POST", headers });
    if (!res.ok) {
      let detail = "";
      try {
        detail = (await res.json())?.detail;
      } catch {}
      throw new Error(detail || `HTTP ${res.status}`);
    }
  }

  async function markMessageUnread(messageId) {
    const res = await fetchWithAuth(`${API_BASE}/messages/message/${messageId}/mark_unread`, { method: "POST", headers });
    if (!res.ok) {
      let detail = "";
      try {
        detail = (await res.json())?.detail;
      } catch {}
      throw new Error(detail || `HTTP ${res.status}`);
    }
  }

  async function markThreadUnread(threadId) {
    const res = await fetchWithAuth(`${API_BASE}/messages/thread/${threadId}/mark_unread`, { method: "POST", headers });
    if (!res.ok) {
      let detail = "";
      try {
        detail = (await res.json())?.detail;
      } catch {}
      throw new Error(detail || `HTTP ${res.status}`);
    }
  }

  async function markThreadRead(threadId) {
    const res = await fetchWithAuth(`${API_BASE}/messages/thread/${threadId}/mark_read`, { method: "POST", headers });
    if (!res.ok) {
      let detail = "";
      try {
        detail = (await res.json())?.detail;
      } catch {}
      throw new Error(detail || `HTTP ${res.status}`);
    }
  }

  async function starThread(threadId) {
    const res = await fetchWithAuth(`${API_BASE}/messages/thread/${threadId}/star`, { method: "POST", headers });
    if (!res.ok) throw new Error();
  }

  async function unstarThread(threadId) {
    const res = await fetchWithAuth(`${API_BASE}/messages/thread/${threadId}/star`, { method: "DELETE", headers });
    if (!res.ok) throw new Error();
  }

  async function handleToggleStar(threadId, currentlyStarred) {
    try {
      if (currentlyStarred) await unstarThread(threadId);
      else await starThread(threadId);
      setThreads((prev) => prev.map((t) => (t.thread_id === threadId ? { ...t, is_starred: !currentlyStarred } : t)));
    } catch {
      showError(`Could not ${currentlyStarred ? "unstar" : "star"} thread`);
    }
  }

  async function handleUnreadMessageClick(msg) {
    if (trashMode) return;
    if (!currentUserId) return;
    if (msg.recipient_id !== currentUserId) return;
    if (msg.sender_id === currentUserId) return;
    if (msg.is_read !== false) return;

    setMessages((prev) => prev.map((m) => (m.id === msg.id ? { ...m, is_read: true } : m)));
    try {
      await markMessageRead(msg.id);
      await loadThreads(page, pageSize, trashMode, { silent: true });
      await loadThread(selectedThread, { silent: true, scroll: false });
    } catch (e) {
      setMessages((prev) => prev.map((m) => (m.id === msg.id ? { ...m, is_read: false } : m)));
      showError(`Could not mark message read: ${e.message || e}`);
    }
  }

  async function handleMarkMessageUnread(msg) {
    if (trashMode) return;
    if (!currentUserId) return;
    if (msg.recipient_id !== currentUserId) return;
    if (msg.sender_id === currentUserId) return;

    setMessages((prev) => prev.map((m) => (m.id === msg.id ? { ...m, is_read: false } : m)));
    try {
      await markMessageUnread(msg.id);
      await loadThreads(page, pageSize, trashMode, { silent: true });
      await loadThread(selectedThread, { silent: true, scroll: false });
    } catch (e) {
      showError(e.message || "Could not mark message unread");
      await loadThread(selectedThread, { silent: true, scroll: false });
      await loadThreads(page, pageSize, trashMode, { silent: true });
    }
  }

  async function markAllAsRead() {
    if (!selectedThread || trashMode) return;

    try {
      await markThreadRead(selectedThread);
      await loadThreads(page, pageSize, trashMode, { silent: true });
      await loadThread(selectedThread, { silent: true, scroll: false });
    } catch {
      showError("Could not mark all as read");
    } finally {
      setShowMarkAllReadConfirm(false);
    }
  }

  async function handleMarkThreadUnread(threadId, contextMessages = null) {
    if (contextMessages) {
      const lastInbound = [...contextMessages]
        .slice()
        .reverse()
        .find((m) => currentUserId && m.recipient_id === currentUserId && m.sender_id !== currentUserId);
      if (!lastInbound) {
        showError(NO_INBOUND_UNREAD_MSG);
        return;
      }
    }

    try {
      await markThreadUnread(threadId);
      await loadThreads(page, pageSize, trashMode, { silent: true });
      if (selectedThread === threadId) await loadThread(threadId, { silent: true, scroll: false });
    } catch (e) {
      showError(e.message || "Could not mark thread unread");
    }
  }

  async function confirmTrashSelected() {
    if (!selectedIds.length) {
      setShowTrashSelectedConfirm(false);
      return;
    }
    showError("");
    try {
      await Promise.all(
        selectedIds.map((threadId) =>
          fetchWithAuth(`${API_BASE}/messages/thread/${threadId}/trash`, { method: "POST", headers })
        )
      );
      setSelectedIds([]);
      await loadThreads(page, pageSize, trashMode, { silent: true });
    } catch {
      showError("Could not trash threads");
    } finally {
      setShowTrashSelectedConfirm(false);
    }
  }

  async function markSelectedUnread() {
    if (!selectedIds.length || trashMode) return;
    showError("");
    try {
      await Promise.all(selectedIds.map((threadId) => markThreadUnread(threadId)));
      await loadThreads(page, pageSize, trashMode, { silent: true });
    } catch (e) {
      showError(e.message || "Could not mark selected threads unread");
    }
  }

  async function markSelectedRead() {
    if (!selectedIds.length || trashMode) return;
    showError("");
    try {
      await Promise.all(selectedIds.map((threadId) => markThreadRead(threadId)));
      await loadThreads(page, pageSize, trashMode, { silent: true });
    } catch (e) {
      showError(e.message || "Could not mark selected threads read");
    }
  }

  async function restoreSelected() {
    if (!selectedIds.length) return;
    showError("");
    try {
      await Promise.all(
        selectedIds.map((threadId) =>
          fetchWithAuth(`${API_BASE}/messages/thread/${threadId}/restore`, { method: "POST", headers })
        )
      );
      setSelectedIds([]);
      await loadThreads(page, pageSize, trashMode, { silent: true });
    } catch {
      showError("Could not restore threads");
    }
  }

  async function permanentlyDeleteSelected() {
    if (!selectedIds.length) {
      setShowPermanentDeleteSelectedConfirm(false);
      return;
    }
    showError("");
    try {
      await Promise.all(
        selectedIds.map((threadId) =>
          fetchWithAuth(`${API_BASE}/messages/thread/${threadId}/permanent`, { method: "DELETE", headers })
        )
      );
      setSelectedIds([]);
      await loadThreads(page, pageSize, trashMode, { silent: true });
    } catch {
      showError("Could not permanently delete threads");
    } finally {
      setShowPermanentDeleteSelectedConfirm(false);
    }
  }

  async function emptyTrash() {
    showError("");
    try {
      const res = await fetchWithAuth(`${API_BASE}/messages/trash/empty`, { method: "POST", headers });
      if (!res.ok) throw new Error();
      setSelectedIds([]);
      setPage(1);
      await loadThreads(1, pageSize, true, { silent: true });
    } catch {
      showError("Could not empty trash");
    } finally {
      setShowEmptyTrashConfirm(false);
    }
  }

  function toggleSelect(threadId) {
    setSelectedIds((ids) => (ids.includes(threadId) ? ids.filter((id) => id !== threadId) : [...ids, threadId]));
  }

  async function trashMessage(messageId) {
    try {
      const res = await fetchWithAuth(`${API_BASE}/messages/message/${messageId}/trash`, { method: "POST", headers });
      if (!res.ok) throw new Error();
      await loadThread(selectedThread, { silent: true, scroll: false });
      await loadThreads(page, pageSize, trashMode, { silent: true });
    } catch {
      showError("Could not trash message");
    }
  }

  function permanentlyDeleteMessage(messageId) {
    setPermanentDeleteTarget({ kind: "message", id: messageId });
  }

  async function confirmPermanentDelete() {
    const target = permanentDeleteTarget;
    if (!target || permanentDeleteBusy) return;
    setPermanentDeleteBusy(true);
    try {
      if (target.kind === "message") {
        const res = await fetchWithAuth(`${API_BASE}/messages/message/${target.id}/permanent`, { method: "DELETE", headers });
        if (!res.ok) throw new Error();
        await loadThread(selectedThread, { silent: true, scroll: false });
        await loadThreads(page, pageSize, trashMode, { silent: true });
      } else {
        await fetchWithAuth(`${API_BASE}/messages/thread/${target.id}/permanent`, { method: "DELETE", headers });
        await loadThreads(page, pageSize, trashMode, { silent: true });
      }
    } catch {
      showError(target.kind === "message" ? "Could not permanently delete message" : "Could not permanently delete thread");
    } finally {
      setPermanentDeleteBusy(false);
      setPermanentDeleteTarget(null);
    }
  }

  // Rendered in both the thread view and the list view (each has its own return).
  const permanentDeleteDialog = (
    <ConfirmDialog
      open={!!permanentDeleteTarget}
      title={
        permanentDeleteTarget?.kind === "thread"
          ? "Permanently delete this thread?"
          : "Permanently delete this message?"
      }
      body="This cannot be undone."
      confirmLabel="Delete permanently"
      tone="danger"
      busy={permanentDeleteBusy}
      onConfirm={confirmPermanentDelete}
      onCancel={() => setPermanentDeleteTarget(null)}
    />
  );

  async function restoreMessage(messageId) {
    try {
      const res = await fetchWithAuth(`${API_BASE}/messages/message/${messageId}/restore`, { method: "POST", headers });
      if (!res.ok) throw new Error();
      await loadThread(selectedThread, { silent: true, scroll: false });
      await loadThreads(page, pageSize, trashMode, { silent: true });
    } catch {
      showError("Could not restore message");
    }
  }

  function handlePageChange(next) {
    setPage((pg) => Math.max(1, pg + (next ? 1 : -1)));
  }

  function handlePageSizeChange(e) {
    setPageSize(Number(e.target.value));
    setPage(1);
  }

  async function refreshNow() {
    if (isRefreshing) return;
    const startedAt = Date.now();
    setIsRefreshing(true);
    showError("");
    try {
      await refreshBlockedListSilent();
      if (selectedThread) await loadThread(selectedThread, { silent: true, scroll: false });
      else await loadThreads(page, pageSize, trashMode, { silent: true });
    } finally {
      const elapsed = Date.now() - startedAt;
      if (elapsed < MIN_REFRESH_SPIN_MS) {
        await new Promise((resolve) => setTimeout(resolve, MIN_REFRESH_SPIN_MS - elapsed));
      }
      setIsRefreshing(false);
    }
  }

  async function pollTick() {
    if (document.visibilityState !== "visible") return;
    try {
      // Retry the account fetch until it lands. Cheap (it stops the moment we have an id) and it
      // is what stops one transient failure from disabling the pinned card for the whole session.
      if (!currentUserId) setMeRetryTick((n) => n + 1);
      refreshBlockedListSilent();
      if (selectedThread) await loadThread(selectedThread, { silent: true, scroll: false });
      else await loadThreads(page, pageSize, trashMode, { silent: true });
    } catch {}
  }

  // Re-point the ref after every render so the poll always refetches the section the user is
  // looking at *now*. The section + filters (work orders / archived / trash / unread / starred)
  // live in state, and an interval that closed over them would keep repainting the list with
  // whichever section was active when the timer was created - e.g. switching Inbox -> Work
  // Orders only changes `workOrderOnly`, so a deps-based interval wouldn't be rebuilt and its
  // next tick would drop the whole unfiltered inbox back into the work-order list.
  useEffect(() => {
    pollTickRef.current = pollTick;
  });

  useEffect(() => {
    if (!token) return;

    const runPoll = () => pollTickRef.current?.();

    function onVisibilityChange() {
      if (document.visibilityState === "visible") runPoll();
    }

    if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    pollTimerRef.current = setInterval(runPoll, 15000);
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [token]);

  // A just-sent video shows a "processing" placeholder while its metadata (GPS) is stripped
  // server-side (a few seconds). The 15s poll is too slow to feel responsive, so while anything
  // in the open thread is still processing, poll quickly until it flips to the playable video.
  const hasProcessingAttachment = useMemo(
    () =>
      messages.some(
        (m) => Array.isArray(m.attachments) && m.attachments.some((a) => a.processing)
      ),
    [messages]
  );

  useEffect(() => {
    if (!token || !selectedThread || !hasProcessingAttachment) return;
    const timer = setInterval(() => {
      if (document.visibilityState === "visible") {
        loadThread(selectedThread, { silent: true, scroll: false });
      }
    }, 3000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, selectedThread, hasProcessingAttachment]);

  // Same idea for demo sessions: the counterpart's reply is written by a background task a few
  // seconds after a send, and waiting up to 15s for the poll to notice makes the demo feel broken.
  //
  // The condition is three-way so it switches itself off - it needs a demo token, an open thread,
  // AND a newest message that is the visitor's own and under 30s old. The moment the reply lands
  // the newest message is no longer theirs and this stops; if no reply ever comes it stops on its
  // own after 30s. Nothing here assumes a reply is actually coming, so it costs nothing outside
  // the demo.
  // This deliberately yields the SEND TIME rather than a boolean, and the 30s deadline is checked
  // inside the interval below. A boolean computed here would be a bug: silent polls run the result
  // through reconcileThreadMessages, which returns the previous array by reference when nothing
  // changed, so `messages` does not change identity while we are waiting - the memo would never
  // recompute and a `Date.now()` test in here would stay true forever, leaving a permanent 3s poll.
  const pendingDemoSendAt = useMemo(() => {
    if (!isDemoToken(token) || messages.length === 0 || !currentUserId) return null;
    const newest = messages[messages.length - 1];
    if (String(newest.sender_id) !== String(currentUserId)) return null;
    const sentAt = new Date(newest.timestamp).getTime();
    return Number.isFinite(sentAt) ? sentAt : null;
  }, [token, messages, currentUserId]);

  useEffect(() => {
    if (!token || !selectedThread || pendingDemoSendAt === null) return;
    const timer = setInterval(() => {
      // The interval owns the deadline, so it stops on time whether or not a re-render happens.
      if (Date.now() - pendingDemoSendAt >= 30000) {
        clearInterval(timer);
        return;
      }
      if (document.visibilityState === "visible") {
        loadThread(selectedThread, { silent: true, scroll: false });
      }
    }, 3000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, selectedThread, pendingDemoSendAt]);

  // Section (inbox / work orders / archived / trash) and the unread + starred filters are all
  // applied server-side now, so `threads` already contains only rows for the current view.
  // Search stays client-side, which is why it remains a "this page only" match.
  const filteredThreads = useMemo(() => {
    const q = (searchQuery || "").trim().toLowerCase();
    if (!q) return threads;

    return threads.filter((t) => {
      const name = String(t.opposing_user || "").toLowerCase();
      const last = String(t.last_message || "").toLowerCase();
      const pinned = String(t.pinned_summary || "").toLowerCase();
      return name.includes(q) || last.includes(q) || pinned.includes(q);
    });
  }, [threads, searchQuery]);



  const groupedWorkOrderThreads = useMemo(() => {
    if (!workOrderOnly || archivedWorkOrderOnly || trashMode) return null;
    const groups = { pending: [], active: [], closed: [] };
    for (const t of filteredThreads) {
      const st = String(t.work_order_status || "").toLowerCase();
      if (st === "pending") groups.pending.push(t);
      else if (["closed", "rejected", "completed"].includes(st)) groups.closed.push(t);
      else groups.active.push(t);
    }
    return groups;
  }, [filteredThreads, workOrderOnly, archivedWorkOrderOnly, trashMode]);

  function goBackToThreads() {
    setSelectedThread(null);
    setMessages([]);
    showError("");
  }

  async function startWorkOrderForThread() {
    if (!selectedThread) return;
    const res = await fetchWithAuth(`${API_BASE}/messages/threads/${selectedThread}/work-order/start`, {
      method: "POST",
      headers,
    });
    if (!res.ok) {
      showError("Could not start work order.");
      return;
    }
    await loadThreads(page, pageSize, trashMode, { silent: true });
  }

  async function requestWorkOrderStatusChange() {
    if (!selectedThread) return;
    const requested_status = workOrderStatusChoice;
    const res = await fetchWithAuth(`${API_BASE}/messages/threads/${selectedThread}/work-order/status-request`, {
      method: "POST",
      headers,
      body: JSON.stringify({ requested_status, note: workOrderStatusNote.trim() || null }),
    });
    if (!res.ok) {
      showError("Could not request work order status change.");
      return;
    }
    setShowWorkOrderStatusModal(false);
    await loadThreads(page, pageSize, trashMode, { silent: true });
  }

  async function updateWorkOrderStatusAsShop() {
    if (!selectedThread) return;
    const status = workOrderStatusChoice;
    const res = await fetchWithAuth(`${API_BASE}/messages/threads/${selectedThread}/work-order/status`, {
      method: "PATCH",
      headers,
      body: JSON.stringify({
        status,
        note: workOrderStatusNote.trim() || null,
        scheduled_at: (status === "scheduled" && workOrderScheduledAt) ? new Date(workOrderScheduledAt).toISOString() : null,
      }),
    });
    if (!res.ok) {
      showError("Could not update work order status.");
      return;
    }
    setShowWorkOrderStatusModal(false);
    await loadThreads(page, pageSize, trashMode, { silent: true });
  }

  async function decideWorkOrderAsShop(decision) {
    if (!selectedThread || workOrderBusy) return;
    setWorkOrderBusy(true);
    try {
      const res = await fetchWithAuth(`${API_BASE}/messages/threads/${selectedThread}/work-order/decision`, {
        method: "POST",
        headers,
        body: JSON.stringify({ decision }),
      });
      if (!res.ok) {
        showError(`Could not ${decision} work order.`);
        return;
      }
      await loadThreads(page, pageSize, trashMode, { silent: true });
    } finally {
      setWorkOrderBusy(false);
    }
  }

  async function archiveWorkOrderFromThread() {
    if (!selectedThread) return;
    const res = await fetchWithAuth(`${API_BASE}/messages/threads/${selectedThread}/work-order/archive`, { method: "POST", headers });
    if (!res.ok) {
      showError("Could not archive work order.");
      return;
    }
    await loadThreads(page, pageSize, trashMode, { silent: true });
    await loadThread(selectedThread, { silent: true, scroll: false });
  }

  async function restoreWorkOrderFromThread() {
    if (!selectedThread) return;
    const res = await fetchWithAuth(`${API_BASE}/messages/threads/${selectedThread}/work-order/restore`, { method: "POST", headers });
    if (!res.ok) {
      showError("Could not restore work order.");
      return;
    }
    await loadThreads(page, pageSize, trashMode, { silent: true });
    await loadThread(selectedThread, { silent: true, scroll: false });
  }

  async function clearWorkOrderFromThread() {
    if (!selectedThread) return;
    const res = await fetchWithAuth(`${API_BASE}/messages/threads/${selectedThread}/work-order`, { method: "DELETE", headers });
    if (!res.ok) return;
    await loadThreads(page, pageSize, trashMode, { silent: true });
  }

  function openWorkOrderStatusModal(actionType) {
    setWorkOrderStatusActionType(actionType);
    setWorkOrderStatusChoice(actionType === "request" ? "scheduled" : (selectedThreadWorkOrderStatus || "quoted"));
    setWorkOrderStatusNote("");
    setWorkOrderScheduledAt("");
    setShowWorkOrderStatusModal(true);
  }

  // THREAD VIEW
  if (selectedThread) {
    const firstUnreadIndex =
      !trashMode && currentUserId
        ? messages.findIndex((m) => m.recipient_id === currentUserId && m.sender_id !== currentUserId && m.is_read === false)
        : -1;

    const hasAnyUnread = firstUnreadIndex !== -1;

    return (
      <div className="p-4">
        <style>{`@keyframes gc-refresh-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
        {permanentDeleteDialog}
        {/* A non-null card is itself proof the other user is a shop - the endpoint 404s for
            everyone else - so this must not also require the thread-list flag. */}
        {pinnedShopCard ? (
          <div className="mb-3">
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">Shop</div>
            <ShopMatchCard shop={pinnedShopCard} mapsUrl={getShopGoogleMapsUrl(pinnedShopCard)} />
          </div>
        ) : null}
        {!trashMode && selectedThreadWorkOrderActive ? (
          <div className={`mb-3 rounded-xl border p-3 ${WORK_ORDER_CARD_COLORS[selectedThreadWorkOrderStatus || "accepted"] || "border-blue-200 dark:border-blue-900 bg-blue-50 dark:bg-blue-950/40 text-blue-900 dark:text-blue-200"}`}>
            <div className="flex items-center justify-between gap-2">
              <div className="text-sm font-semibold text-blue-900 dark:text-blue-200">Work Order Status: {String(selectedThreadWorkOrderStatus || "accepted").replace("_", " ")}</div>
              {selectedThreadWorkOrderRequestedStatus ? (
                <div className="text-xs text-amber-900 dark:text-amber-200 bg-amber-100 dark:bg-gray-900 border border-amber-200 dark:border-amber-700 rounded px-2 py-0.5">
                  Requested: {String(selectedThreadWorkOrderRequestedStatus).replace("_", " ")}
                </div>
              ) : null}
            </div>
            <div className="mt-1 text-xs text-blue-900/80 dark:text-blue-200/80">
              Last update: {selectedThreadWorkOrderLastUpdatedAt ? new Date(selectedThreadWorkOrderLastUpdatedAt).toLocaleString() : "-"}
            </div>
            {selectedThreadWorkOrderScheduledAt ? (
              <div className="mt-1 text-xs text-blue-900/80 dark:text-blue-200/80">
                Appointment: {new Date(selectedThreadWorkOrderScheduledAt).toLocaleString()}
              </div>
            ) : null}
            <div className="mt-3 flex flex-wrap items-center gap-2">
              {selectedThreadOtherUserIsShop && !selectedThreadUserDeleted && !["pending", "rejected"].includes(selectedThreadWorkOrderStatus || "") ? (
                <button onClick={() => openWorkOrderStatusModal("request")} className="px-3 py-2 rounded-xl bg-amber-500 text-white text-sm">
                  Request Status Change
                </button>
              ) : null}
              {!selectedThreadOtherUserIsShop && !selectedThreadUserDeleted && !["pending", "rejected"].includes(selectedThreadWorkOrderStatus || "") ? (
                <button onClick={() => openWorkOrderStatusModal("update")} className="px-3 py-2 rounded-xl bg-emerald-600 text-white text-sm">
                  Update Work Order Status
                </button>
              ) : null}
              {!selectedThreadOtherUserIsShop && selectedThreadWorkOrderStatus === "pending" ? (
                <>
                  <button onClick={() => decideWorkOrderAsShop("accepted")} disabled={workOrderBusy} className="px-3 py-2 rounded-xl bg-green-600 text-white text-sm disabled:opacity-60 disabled:cursor-not-allowed">Accept Work Order</button>
                  <button onClick={() => decideWorkOrderAsShop("rejected")} disabled={workOrderBusy} className="px-3 py-2 rounded-xl bg-red-600 text-white text-sm disabled:opacity-60 disabled:cursor-not-allowed">Reject Work Order</button>
                </>
              ) : null}
              {!selectedThreadMeta?.work_order_archived ? (
                <button onClick={() => setShowCancelWorkOrderConfirm(true)} className="px-3 py-2 rounded-xl bg-gray-700 text-white text-sm">Cancel Work Order</button>
              ) : null}
              {!selectedThreadOtherUserIsShop && !selectedThreadMeta?.work_order_archived && ["completed", "closed", "rejected"].includes(selectedThreadWorkOrderStatus || "") ? (
                <button onClick={() => setShowArchiveWorkOrderConfirm(true)} className="px-3 py-2 rounded-xl bg-slate-700 text-white text-sm">Archive Work Order</button>
              ) : null}
              {!selectedThreadOtherUserIsShop && selectedThreadMeta?.work_order_archived ? (
                <button onClick={() => setShowRestoreWorkOrderConfirm(true)} className="px-3 py-2 rounded-xl bg-cyan-700 text-white text-sm">Restore Work Order</button>
              ) : null}
            </div>
          </div>
        ) : null}

        <div className="flex items-center gap-2 mb-2">
          <h2 className="text-xl font-bold">Thread</h2>
          {!selectedThreadUserDeleted && selectedThreadMeta?.opposing_user ? (
            <PresenceIndicator online={selectedThreadOpposingOnline} lastSeen={selectedThreadOpposingLastSeen} className="text-xs" />
          ) : null}
          {selectedThreadUserDeleted && (
            <span className="text-xs font-semibold bg-amber-100 dark:bg-gray-900 text-amber-900 dark:text-amber-200 px-2 py-1 rounded">Deleted user • Read-only</span>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2 mb-4">
          <button
            onClick={goBackToThreads}
            className="bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-900 dark:text-gray-100 w-10 h-10 rounded-xl inline-flex items-center justify-center"
            title="Back to threads"
            aria-label="Back to threads"
          >
            <i className="fi fi-rr-angle-left text-lg leading-none not-italic" />
          </button>

          {!trashMode && (
            <button
              onClick={() => (hasAnyUnread ? setShowMarkAllReadConfirm(true) : handleMarkThreadUnread(selectedThread, messages))}
              className="bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-900 dark:text-gray-100 w-10 h-10 rounded-xl inline-flex items-center justify-center"
              title={hasAnyUnread ? "Mark all as read" : "Mark thread unread"}
              aria-label={hasAnyUnread ? "Mark all as read" : "Mark thread unread"}
            >
              {hasAnyUnread ? (
                <Icon name="envelope-dot" variant="solidRounded" className="text-lg leading-none" />
              ) : (
                <Icon name="envelope" variant="regularRounded" className="text-lg leading-none" />
              )}
            </button>
          )}

          {!trashMode && threadOtherUserId && !selectedThreadUserDeleted && (
            <button
              onClick={() => setShowBlockToggleConfirm(true)}
              className="bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-900 dark:text-gray-100 w-10 h-10 rounded-xl inline-flex items-center justify-center"
              title={isOtherUserBlocked ? "Unblock user" : "Block user"}
              aria-label={isOtherUserBlocked ? "Unblock user" : "Block user"}
            >
              {isOtherUserBlocked ? (
                <UserBlockedIcon />
              ) : (
                <UserIcon />
              )}
            </button>
          )}

          {!trashMode && threadOtherUserId && !selectedThreadUserDeleted && (
            <button
              onClick={openReportModal}
              className="bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-900 dark:text-gray-100 w-10 h-10 rounded-xl inline-flex items-center justify-center"
              title="Report user"
              aria-label="Report user"
            >
              <i className="fi fi-rr-flag text-lg leading-none not-italic" />
            </button>
          )}

          {/* ✅ UPDATED: star button now matches height + is wider */}
          {!trashMode && (
            <button
              type="button"
              title={selectedThreadIsStarred ? "Unstar thread" : "Star thread"}
              onClick={() => handleToggleStar(selectedThread, selectedThreadIsStarred)}
              className={
                "bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 w-10 h-10 rounded-xl flex items-center justify-center " +
                "focus:outline-none focus:ring-2 focus:ring-black/20 " +
                (selectedThreadIsStarred ? "text-yellow-500" : "text-gray-500 dark:text-gray-400")
              }
            >
              <InboxThreadStarIcon starred={selectedThreadIsStarred} className="text-xl leading-none" />
            </button>
          )}

          {!trashMode && threadOtherUserId && selectedThreadOtherUserIsShop && !selectedThreadUserDeleted && (
            <button
              type="button"
              onClick={toggleFavoriteForThreadUser}
              className={`bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 w-10 h-10 rounded-xl inline-flex items-center justify-center ${
                isOtherUserFavorited ? "text-red-600 dark:text-red-400" : "text-gray-700 dark:text-gray-200"
              }`}
              title={isOtherUserFavorited ? "Unfavorite shop" : "Favorite shop"}
              aria-label={isOtherUserFavorited ? "Unfavorite shop" : "Favorite shop"}
            >
              <i className={`not-italic text-lg ${isOtherUserFavorited ? "fi fi-sr-heart" : "fi fi-rs-heart fi-rr-heart"}`} />
            </button>
          )}

          <RefreshButton onClick={refreshNow} isRefreshing={isRefreshing} />
          {!trashMode && !selectedThreadWorkOrderActive && selectedThreadOtherUserIsShop && !selectedThreadUserDeleted ? (
            <button onClick={() => setShowStartWorkOrderConfirm(true)} className="px-3 py-2 rounded-xl bg-blue-600 text-white text-sm">
              Start Work Order
            </button>
          ) : null}
        </div>
        {error && <div className="text-red-600 dark:text-red-400 mb-2">{error}</div>}

        {showCancelWorkOrderConfirm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" role="dialog" aria-modal="true">
            <div className="w-full max-w-sm rounded-2xl bg-white dark:bg-gray-800 p-5 shadow-xl">
              <p className="text-base font-semibold text-gray-900 dark:text-gray-100">Cancel work order for this thread?</p>
              <p className="mt-2 text-sm text-gray-700 dark:text-gray-200">This removes work order tracking and returns this conversation to a normal thread.</p>
              <div className="mt-4 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={async () => {
                    setShowCancelWorkOrderConfirm(false);
                    await clearWorkOrderFromThread();
                  }}
                  className="bg-green-500 text-white px-4 py-2 rounded-xl inline-flex items-center justify-center gap-2 hover:brightness-95 active:scale-[0.98]"
                >
                  <i className="fi fi-rr-check not-italic" />
                  Yes
                </button>
                <button
                  type="button"
                  onClick={() => setShowCancelWorkOrderConfirm(false)}
                  className="bg-red-500 text-white px-4 py-2 rounded-xl inline-flex items-center justify-center gap-2 hover:brightness-95 active:scale-[0.98]"
                >
                  <i className="fi fi-rr-cross not-italic" />
                  No
                </button>
              </div>
            </div>
          </div>
        )}


        {showArchiveWorkOrderConfirm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" role="dialog" aria-modal="true">
            <div className="w-full max-w-sm rounded-2xl bg-white dark:bg-gray-800 p-5 shadow-xl">
              <p className="text-base font-semibold text-gray-900 dark:text-gray-100">Archive work order for this thread?</p>
              <p className="mt-2 text-sm text-gray-700 dark:text-gray-200">This hides it from active work order view and moves it to Archived Work Orders.</p>
              <div className="mt-4 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={async () => {
                    setShowArchiveWorkOrderConfirm(false);
                    await archiveWorkOrderFromThread();
                  }}
                  className="bg-green-500 text-white px-4 py-2 rounded-xl inline-flex items-center justify-center gap-2 hover:brightness-95 active:scale-[0.98]"
                >
                  <i className="fi fi-rr-check not-italic" />
                  Yes
                </button>
                <button
                  type="button"
                  onClick={() => setShowArchiveWorkOrderConfirm(false)}
                  className="bg-red-500 text-white px-4 py-2 rounded-xl inline-flex items-center justify-center gap-2 hover:brightness-95 active:scale-[0.98]"
                >
                  <i className="fi fi-rr-cross not-italic" />
                  No
                </button>
              </div>
            </div>
          </div>
        )}

        {showRestoreWorkOrderConfirm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" role="dialog" aria-modal="true">
            <div className="w-full max-w-sm rounded-2xl bg-white dark:bg-gray-800 p-5 shadow-xl">
              <p className="text-base font-semibold text-gray-900 dark:text-gray-100">Restore work order for this thread?</p>
              <p className="mt-2 text-sm text-gray-700 dark:text-gray-200">This returns it to active work order view.</p>
              <div className="mt-4 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={async () => {
                    setShowRestoreWorkOrderConfirm(false);
                    await restoreWorkOrderFromThread();
                  }}
                  className="bg-green-500 text-white px-4 py-2 rounded-xl inline-flex items-center justify-center gap-2 hover:brightness-95 active:scale-[0.98]"
                >
                  <i className="fi fi-rr-check not-italic" />
                  Yes
                </button>
                <button
                  type="button"
                  onClick={() => setShowRestoreWorkOrderConfirm(false)}
                  className="bg-red-500 text-white px-4 py-2 rounded-xl inline-flex items-center justify-center gap-2 hover:brightness-95 active:scale-[0.98]"
                >
                  <i className="fi fi-rr-cross not-italic" />
                  No
                </button>
              </div>
            </div>
          </div>
        )}

        {showStartWorkOrderConfirm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" role="dialog" aria-modal="true">
            <div className="w-full max-w-sm rounded-2xl bg-white dark:bg-gray-800 p-5 shadow-xl">
              <p className="text-base font-semibold text-gray-900 dark:text-gray-100">Start work order for this thread?</p>
              <p className="mt-2 text-sm text-gray-700 dark:text-gray-200">This will move this conversation into work order tracking for this shop.</p>
              <div className="mt-4 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={async () => {
                    setShowStartWorkOrderConfirm(false);
                    await startWorkOrderForThread();
                  }}
                  className="bg-green-500 text-white px-4 py-2 rounded-xl inline-flex items-center justify-center gap-2 hover:brightness-95 active:scale-[0.98]"
                >
                  <i className="fi fi-rr-check not-italic" />
                  Yes
                </button>
                <button
                  type="button"
                  onClick={() => setShowStartWorkOrderConfirm(false)}
                  className="bg-red-500 text-white px-4 py-2 rounded-xl inline-flex items-center justify-center gap-2 hover:brightness-95 active:scale-[0.98]"
                >
                  <i className="fi fi-rr-cross not-italic" />
                  No
                </button>
              </div>
            </div>
          </div>
        )}

        {showMarkAllReadConfirm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" role="dialog" aria-modal="true">
            <div className="w-full max-w-sm rounded-2xl bg-white dark:bg-gray-800 p-5 shadow-xl">
              <p className="text-base font-semibold text-gray-900 dark:text-gray-100">Mark all messages as read?</p>
              <div className="mt-4 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={markAllAsRead}
                  className="bg-green-500 text-white px-4 py-2 rounded-xl inline-flex items-center justify-center gap-2 hover:brightness-95 active:scale-[0.98]"
                >
                  <i className="fi fi-rr-check not-italic" />
                  Yes
                </button>
                <button
                  type="button"
                  onClick={() => setShowMarkAllReadConfirm(false)}
                  className="bg-red-500 text-white px-4 py-2 rounded-xl inline-flex items-center justify-center gap-2 hover:brightness-95 active:scale-[0.98]"
                >
                  <i className="fi fi-rr-cross not-italic" />
                  No
                </button>
              </div>
            </div>
          </div>
        )}

        {showBlockToggleConfirm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" role="dialog" aria-modal="true">
            <div className="w-full max-w-sm rounded-2xl bg-white dark:bg-gray-800 p-5 shadow-xl">
              <p className="text-base font-semibold text-gray-900 dark:text-gray-100">
                {isOtherUserBlocked
                  ? "Unblock this user? They will be able to deliver new messages to you"
                  : "Block this user? They will not be able to deliver new messages to you"}
              </p>
              <div className="mt-4 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={toggleBlockForThreadUser}
                  className="bg-green-500 text-white px-4 py-2 rounded-xl inline-flex items-center justify-center gap-2 hover:brightness-95 active:scale-[0.98]"
                >
                  <i className="fi fi-rr-check not-italic" />
                  Yes
                </button>
                <button
                  type="button"
                  onClick={() => setShowBlockToggleConfirm(false)}
                  className="bg-red-500 text-white px-4 py-2 rounded-xl inline-flex items-center justify-center gap-2 hover:brightness-95 active:scale-[0.98]"
                >
                  <i className="fi fi-rr-cross not-italic" />
                  No
                </button>
              </div>
            </div>
          </div>
        )}

        {showReportModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" role="dialog" aria-modal="true">
            <div className="w-full max-w-md rounded-2xl bg-white dark:bg-gray-800 p-5 shadow-xl">
              <p className="text-base font-semibold text-gray-900 dark:text-gray-100">Report this user</p>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                Flag this conversation for our team to review (scams, harassment, or spam).
              </p>
              {reportDone ? (
                <div className="mt-4 text-sm text-gray-800 dark:text-gray-100">{reportDone}</div>
              ) : (
                <div className="mt-3 space-y-3">
                  <label className="block text-sm">
                    <span className="text-gray-700 dark:text-gray-200">Reason</span>
                    <select
                      value={reportCategory}
                      onChange={(e) => setReportCategory(e.target.value)}
                      className="mt-1 w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm"
                    >
                      <option value="scam">Scam / fraud</option>
                      <option value="harassment">Harassment / abuse</option>
                      <option value="spam">Spam</option>
                      <option value="other">Other</option>
                    </select>
                  </label>
                  <label className="block text-sm">
                    <span className="text-gray-700 dark:text-gray-200">Details (optional)</span>
                    <textarea
                      value={reportReason}
                      onChange={(e) => setReportReason(e.target.value)}
                      rows={3}
                      maxLength={2000}
                      placeholder="Add any context that will help our team…"
                      className="mt-1 w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm"
                    />
                  </label>
                </div>
              )}
              <div className="mt-4 flex items-center justify-end gap-2">
                {reportDone ? (
                  <button
                    type="button"
                    onClick={() => setShowReportModal(false)}
                    className="bg-gray-800 text-white px-4 py-2 rounded-xl text-sm"
                  >
                    Close
                  </button>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={() => setShowReportModal(false)}
                      className="px-4 py-2 rounded-xl text-sm border border-gray-300 dark:border-gray-600"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      disabled={reportSubmitting}
                      onClick={submitReport}
                      className="bg-red-600 text-white px-4 py-2 rounded-xl text-sm disabled:opacity-50"
                    >
                      {reportSubmitting ? "Submitting…" : "Submit report"}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {showWorkOrderStatusModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" role="dialog" aria-modal="true">
            <div className="w-full max-w-lg rounded-2xl bg-white dark:bg-gray-800 p-5 shadow-xl">
              <p className="text-base font-semibold text-gray-900 dark:text-gray-100">
                {workOrderStatusActionType === "request" ? "Request work order status change" : "Update work order status"}
              </p>
              <div className="mt-3 flex flex-col gap-2">
                {WORK_ORDER_STATUS_OPTIONS.map((statusOpt) => (
                  <label
                    key={statusOpt.value}
                    className={`border rounded-lg px-3 py-2 text-sm cursor-pointer transition ${
                      workOrderStatusChoice === statusOpt.value
                        ? `${statusOpt.classes} ring-2 ring-blue-300`
                        : "bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200"
                    }`}
                  >
                    <input
                      type="radio"
                      name="work-order-status-choice"
                      className="mr-2"
                      checked={workOrderStatusChoice === statusOpt.value}
                      onChange={() => setWorkOrderStatusChoice(statusOpt.value)}
                    />
                    {statusOpt.label}
                  </label>
                ))}
              </div>
              {workOrderStatusActionType === "update" && workOrderStatusChoice === "scheduled" ? (
                <div className="mt-3">
                  <label className="text-xs font-semibold text-gray-700 dark:text-gray-200">Appointment date &amp; time (optional)</label>
                  <input
                    type="datetime-local"
                    className="mt-1 block w-full rounded-xl border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm"
                    value={workOrderScheduledAt}
                    onChange={(e) => setWorkOrderScheduledAt(e.target.value)}
                  />
                </div>
              ) : null}
              <textarea
                value={workOrderStatusNote}
                onChange={(e) => setWorkOrderStatusNote(e.target.value)}
                className="mt-3 w-full border rounded-lg p-2 text-sm"
                rows={3}
                placeholder="Optional note"
              />
              <div className="mt-4 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowWorkOrderStatusModal(false)}
                  className="bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-4 py-2 rounded-xl"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => (workOrderStatusActionType === "request" ? requestWorkOrderStatusChange() : updateWorkOrderStatusAsShop())}
                  className="bg-blue-600 text-white px-4 py-2 rounded-xl"
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-2">
          {messages.map((msg, idx) => {
            const isMine = !!currentUserId && msg.sender_id === currentUserId;

            const isUnreadForMe =
              !trashMode &&
              currentUserId &&
              msg.recipient_id === currentUserId &&
              msg.sender_id !== currentUserId &&
              msg.is_read === false;

            const canMarkUnread =
              !trashMode &&
              currentUserId &&
              msg.recipient_id === currentUserId &&
              msg.sender_id !== currentUserId &&
              msg.is_read !== false;

            const bubbleBase =
              "px-4 py-3 shadow-sm whitespace-pre-wrap break-words text-sm text-gray-800 dark:text-gray-100 border border-gray-200 dark:border-gray-700 rounded-2xl";
            const mineBubble = "bg-green-100 dark:bg-green-950/40";
            const readInboundBubble = "bg-blue-200 dark:bg-blue-950/40";
            const unreadRing = "ring-2 ring-yellow-300 bg-yellow-50 dark:bg-gray-900 cursor-pointer";
            const bubbleClass = `${bubbleBase} ${isMine ? mineBubble : readInboundBubble} ${isUnreadForMe ? unreadRing : ""}`;

            const dayLabel = formatDayDivider(msg.timestamp);
            const prevDayLabel = idx > 0 ? formatDayDivider(messages[idx - 1].timestamp) : null;
            const showDayDivider = !!dayLabel && dayLabel !== prevDayLabel;

            return (
              <React.Fragment key={msg.id}>
                {showDayDivider && (
                  <div className="my-3 flex items-center gap-3" aria-hidden="true">
                    <div className="h-px bg-gray-200 dark:bg-gray-700 flex-1" />
                    <div className="text-xs text-gray-500 dark:text-gray-400 px-2">{dayLabel}</div>
                    <div className="h-px bg-gray-200 dark:bg-gray-700 flex-1" />
                  </div>
                )}
                {idx === firstUnreadIndex && (
                  <div className="my-3 flex items-center gap-3">
                    <div className="h-px bg-gray-300 dark:bg-gray-600 flex-1" />
                    <div className="text-xs font-semibold text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 px-2">Unread messages</div>
                    <div className="h-px bg-gray-300 dark:bg-gray-600 flex-1" />
                  </div>
                )}

                <div className={`flex ${isMine ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[90%] sm:max-w-[75%] ${bubbleClass}`}
                    title={isUnreadForMe ? "Unread message (click to mark as read)" : ""}
                    onClick={() => {
                      if (isUnreadForMe) handleUnreadMessageClick(msg);
                    }}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs font-semibold text-gray-900 dark:text-gray-100">{isMine ? "You" : displaySender(msg)}</span>

                      <span className="ml-auto text-xs text-gray-500 dark:text-gray-400" title={formatTimestamp(msg.timestamp)}>{formatTimeOnly(msg.timestamp)}</span>

                      {canMarkUnread && (
                        <span onClick={(e) => e.stopPropagation()}>
                          <IconButton
                            title="Mark message unread"
                            onClick={() => handleMarkMessageUnread(msg)}
                            className="text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                          >
                            <Icon name="envelope" variant="regularRounded" className="text-base leading-none" />
                          </IconButton>
                        </span>
                      )}

                      {!trashMode && (
                        <span onClick={(e) => e.stopPropagation()}>
                          <IconButton title="Trash message" onClick={() => trashMessage(msg.id)} className="text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/40">
                            <Icon name="trash" className="text-base leading-none" />
                          </IconButton>
                        </span>
                      )}

                      {trashMode && (
                        <span onClick={(e) => e.stopPropagation()} className="flex gap-2">
                          <IconButton title="Restore message" onClick={() => restoreMessage(msg.id)} className="text-green-700 dark:text-green-300 hover:bg-green-50">
                            <RestoreIcon />
                          </IconButton>
                          <IconButton title="Delete permanently" onClick={() => permanentlyDeleteMessage(msg.id)} className="text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/40">
                            <Icon name="trash" className="text-base leading-none" />
                          </IconButton>
                        </span>
                      )}
                    </div>

                    {msg.content ? <div>{msg.content}</div> : null}

                    {Array.isArray(msg.attachments) && msg.attachments.length > 0 && (
                      <div className="mt-2 flex flex-col gap-2">
                        {msg.attachments.map((a) => (
                          <div key={a.id} className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white/60 dark:bg-gray-800/60 p-1">
                            {a.processing ? (
                              <div className="flex items-center gap-3 rounded-md bg-gray-50 dark:bg-gray-900 px-3 py-5">
                                <span
                                  className="inline-block h-5 w-5 shrink-0 animate-spin rounded-full border-2 border-gray-300 dark:border-gray-600 border-t-blue-600"
                                  aria-hidden="true"
                                />
                                <span className="flex flex-col leading-tight">
                                  <span className="text-sm font-medium text-gray-700 dark:text-gray-200">Processing video...</span>
                                  <span className="text-xs text-gray-500 dark:text-gray-400">It will appear here in a few seconds.</span>
                                </span>
                              </div>
                            ) : a.expired || !a.url ? (
                              <div className="px-2 py-3 text-xs text-gray-500 dark:text-gray-400 italic">
                                This attachment was removed (attachments expire 30 days after upload).
                              </div>
                            ) : (
                              <>
                                {a.kind === "video" ? (
                                  <video
                                    src={a.url}
                                    controls
                                    preload="metadata"
                                    className="max-h-72 w-full rounded"
                                    onError={() => handleAttachmentError(msg.thread_id)}
                                  />
                                ) : a.kind === "document" ? (
                                  <a
                                    href={a.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-2 rounded px-2 py-3 hover:bg-gray-50 dark:hover:bg-gray-700"
                                  >
                                    <i className="not-italic text-xl text-red-600 dark:text-red-400 fi fi-rr-file-pdf" />
                                    <span className="truncate text-sm text-gray-800 dark:text-gray-100">{a.original_filename || "Document.pdf"}</span>
                                    <span className="ml-auto text-xs font-semibold text-blue-600 dark:text-blue-300">Open</span>
                                  </a>
                                ) : (
                                  <a href={a.url} target="_blank" rel="noopener noreferrer">
                                    <img
                                      src={a.url}
                                      alt={a.original_filename || "attachment"}
                                      loading="lazy"
                                      className="max-h-72 rounded object-contain"
                                      onError={() => handleAttachmentError(msg.thread_id)}
                                    />
                                  </a>
                                )}
                                <div className="mt-1 flex items-center gap-2 px-1 text-[11px] text-gray-500 dark:text-gray-400">
                                  <a href={a.download_url} download={a.original_filename || undefined} className="text-blue-600 dark:text-blue-300 hover:underline">
                                    Download
                                  </a>
                                  <span className="truncate">{a.original_filename}</span>
                                  <span className="ml-auto whitespace-nowrap">Expires {formatTimestamp(a.expires_at)}</span>
                                </div>
                              </>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </React.Fragment>
            );
          })}

          <div ref={bottomRef} />
        </div>

        {!trashMode && (
          <div className="mt-4">
            {selectedThreadUserDeleted && (
              <div className="mb-2 text-sm text-amber-800 dark:text-amber-300 bg-amber-50 dark:bg-gray-900 border border-amber-200 dark:border-amber-700 rounded px-3 py-2">
                This user has deleted their account. You can view message history, but cannot send new messages in this thread.
              </div>
            )}
            {!selectedThreadUserDeleted && !attachmentWarningDismissed && (
              <div className="mb-2 flex items-start gap-2 text-xs text-blue-900 dark:text-blue-200 bg-blue-50 dark:bg-blue-950/40 border border-blue-200 dark:border-blue-900 rounded px-3 py-2">
                <span>Photos, videos, and files are removed 30 days after upload - download anything you want to keep.</span>
                <button onClick={dismissAttachmentWarning} className="ml-auto font-semibold text-blue-700 dark:text-blue-300 hover:text-blue-900" aria-label="Dismiss">×</button>
              </div>
            )}
            <textarea
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              placeholder={selectedThreadUserDeleted ? "Thread is read-only" : "Type your reply..."}
              rows={3}
              disabled={selectedThreadUserDeleted}
              className="w-full border p-2 rounded disabled:bg-gray-100 disabled:text-gray-500"
            />

            {selectedFiles.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {selectedFiles.map((f) => (
                  <div key={f.localId} className="flex items-center gap-2 border rounded-lg px-2 py-1 text-xs bg-white dark:bg-gray-800 max-w-[260px]">
                    <i className={`not-italic ${f.kind === "video" ? "fi fi-rr-play-circle" : f.kind === "document" ? "fi fi-rr-file-pdf" : "fi fi-rr-picture"}`} />
                    <span className="truncate">{f.file.name}</span>
                    <span className="text-gray-400">{formatBytes(f.file.size)}</span>
                    {f.status === "uploading" && <span className="text-blue-600 dark:text-blue-300">{f.progress}%</span>}
                    {f.status === "error" && (
                      <>
                        <span className="text-red-600 dark:text-red-400" title={f.error}>failed</span>
                        <button onClick={() => retrySelectedFile(f.localId)} className="text-blue-700 dark:text-blue-300 hover:underline" type="button">Retry</button>
                      </>
                    )}
                    {f.status === "done" && <i className="not-italic text-green-600 dark:text-green-400 fi fi-rr-check" />}
                    <button onClick={() => removeSelectedFile(f.localId)} className="text-gray-500 dark:text-gray-400 hover:text-red-600" aria-label="Remove">×</button>
                  </div>
                ))}
              </div>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept={ATTACHMENT_ACCEPT}
              multiple
              className="hidden"
              onChange={handleFilesSelected}
            />

            <div className="mt-2 flex items-center gap-2">
              <button
                onClick={goBackToThreads}
                className="bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-900 dark:text-gray-100 w-10 h-10 rounded-xl inline-flex items-center justify-center"
                title="Back to threads"
                aria-label="Back to threads"
              >
                <i className="fi fi-rr-angle-left text-lg leading-none not-italic" />
              </button>
              <button
                onClick={() => fileInputRef.current && fileInputRef.current.click()}
                disabled={selectedThreadUserDeleted}
                className="bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-900 dark:text-gray-100 w-10 h-10 rounded-xl inline-flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                title="Attach photo or video"
                aria-label="Attach photo or video"
              >
                <i className="fi fi-rr-clip text-lg leading-none not-italic" />
              </button>
              <button
                onClick={sendReply}
                disabled={isSending || selectedThreadUserDeleted || selectedFiles.some((f) => f.status === "uploading")}
                className="bg-blue-600 disabled:bg-gray-400 disabled:cursor-not-allowed text-white px-4 py-2 rounded-xl"
              >
                {isSending
                  ? "Sending..."
                  : selectedFiles.some((f) => f.status === "uploading")
                  ? "Uploading…"
                  : selectedFiles.some((f) => f.status === "done")
                  ? "Send Reply with Attachments"
                  : "Send Reply"}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // LIST VIEW (INBOX / TRASH)
  const activeSection = trashMode
    ? "trash"
    : archivedWorkOrderOnly
    ? "archived"
    : workOrderOnly
    ? "work-order"
    : "inbox";
  const messagingSections = [
    { key: "inbox", label: "Inbox", count: counts.unread },
    // Every badge is an UNREAD count so they read consistently; work_order_active (how many
    // work orders exist) is deliberately not used here.
    { key: "work-order", label: "Work Orders", count: counts.work_order_active_unread },
    { key: "archived", label: "Archived Work Orders", count: counts.work_order_archived_unread },
    { key: "trash", label: "Trash", count: 0 },
  ];
  const selectSection = (key) => {
    setUnreadOnly(false);
    setStarredOnly(false);
    setPage(1);
    setTrashMode(key === "trash");
    setWorkOrderOnly(key === "work-order" || key === "archived");
    setArchivedWorkOrderOnly(key === "archived");
  };

  // The inbox row card. Returned as a <div> (not <li>) so it can sit directly in the list or
  // nested inside a request-group "bubble" without invalid <li>-in-<li>. Behavior is unchanged.
  const renderThreadCard = (thread) => {
    const hasUnread = !trashMode && (thread.unread_count || 0) > 0;
    const label = thread.opposing_user || "Conversation";
    const isDeletedUser = !!thread.opposing_user_deleted;
    const tsParts = formatTimestampParts(thread.timestamp);
    const isStarred = !!thread.is_starred;

    return (
      <div
        key={thread.thread_id}
        className={[
          "group border rounded-2xl shadow-sm hover:shadow transition",
          "px-3 py-3 sm:px-4 sm:py-4 cursor-pointer",
          // Each branch owns the row background (no shared bg-* here, so the shades never
          // fight in the cascade). Unread rows: yellow tint in light mode; in dark mode a
          // clearly darker bubble (gray-900 vs the read rows' gray-800) with a blue border,
          // so unread stands out at a glance. Both branches shade on hover.
          hasUnread
            ? "border-yellow-400 bg-yellow-50/60 hover:bg-yellow-100/70 dark:border-blue-700 dark:bg-gray-900 dark:hover:bg-gray-800"
            : "border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700",
        ].join(" ")}
        onClick={() => {
          unlockNotificationSound();
          loadThread(thread.thread_id, { silent: false, scroll: true });
        }}
      >
        <div className="flex items-start gap-3">
          <ThreadSelectToggle
            selected={selectedIds.includes(thread.thread_id)}
            onToggle={() => toggleSelect(thread.thread_id)}
            label={label}
          />

          <div className="min-w-0 flex-1">
            <div className="flex items-start gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 min-w-0">
                  {!isDeletedUser ? (
                    <PresenceIndicator
                      online={thread.opposing_user_online}
                      lastSeen={thread.opposing_user_last_seen_at}
                      showLabel={false}
                    />
                  ) : null}
                  <div className="font-semibold text-gray-900 dark:text-gray-100 truncate">{label}</div>
                </div>

                {isDeletedUser || thread.work_order_active ? (
                  <div className="mt-1 flex flex-wrap items-center gap-1.5">
                    {isDeletedUser && <span className="text-[10px] bg-amber-100 dark:bg-gray-900 text-amber-900 dark:text-amber-200 px-2 py-0.5 rounded">DELETED USER</span>}
                    {thread.work_order_active && !workOrderOnly ? (
                      <span className="text-[10px] bg-blue-100 dark:bg-blue-950/40 text-blue-900 dark:text-blue-200 px-2 py-0.5 rounded">WORK ORDER</span>
                    ) : null}
                    {thread.work_order_active ? (
                      <span className={`text-[10px] px-2 py-0.5 rounded ${WORK_ORDER_STATUS_COLORS[thread.work_order_status || "accepted"] || "bg-blue-100 dark:bg-blue-950/40 text-blue-900 dark:text-blue-200"}`}>
                        {String(thread.work_order_status || "accepted").replace("_", " ")}
                      </span>
                    ) : null}
                  </div>
                ) : null}

                {thread.pinned_summary ? (
                  <div className="mt-1 text-xs text-gray-600 dark:text-gray-300 truncate">
                    <span className="inline-flex items-center px-2 py-0.5 rounded border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 mr-2">
                      Request
                    </span>
                    {formatRequestSummary(thread.pinned_summary)}
                  </div>
                ) : null}

                <div className="mt-1 text-sm text-gray-700 dark:text-gray-200 truncate">{thread.last_message || ""}</div>
              </div>

              <div className="ml-auto text-right shrink-0">
                <div className="text-xs text-gray-500 dark:text-gray-400 leading-tight">
                  <div>{tsParts.date}</div>
                  {tsParts.time ? <div>{tsParts.time}</div> : null}
                </div>

                {hasUnread ? (
                  <div className="mt-1 inline-flex items-center justify-center min-w-[22px] h-5 px-2 rounded-full bg-blue-600 text-white text-xs font-semibold">
                    {thread.unread_count}
                  </div>
                ) : (
                  <div className="mt-1 h-5" />
                )}

                <div className="mt-2 flex items-center justify-end gap-2" onClick={(e) => e.stopPropagation()}>
                  {!trashMode && (
                    <>
                      <IconButton
                        title={isStarred ? "Unstar thread" : "Star thread"}
                        onClick={() => handleToggleStar(thread.thread_id, isStarred)}
                        className={isStarred ? "text-yellow-500 hover:bg-yellow-50" : "text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700"}
                      >
                        <InboxThreadStarIcon starred={isStarred} className="text-base leading-none" />
                      </IconButton>

                      <IconButton
                        title={hasUnread ? "Thread has unread messages" : "Mark thread unread"}
                        onClick={() => handleMarkThreadUnread(thread.thread_id)}
                        className="text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                      >
                        {hasUnread ? (
                          <Icon name="envelope-dot" variant="solidRounded" className="text-base leading-none" />
                        ) : (
                          <Icon name="envelope" variant="regularRounded" className="text-base leading-none" />
                        )}
                      </IconButton>

                      <IconButton
                        title="Trash thread"
                        onClick={async () => {
                          try {
                            await fetchWithAuth(`${API_BASE}/messages/thread/${thread.thread_id}/trash`, { method: "POST", headers });
                            await loadThreads(page, pageSize, trashMode, { silent: true });
                          } catch {
                            showError("Could not trash thread");
                          }
                        }}
                        className="text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/40"
                      >
                        <Icon name="trash" className="text-base leading-none" />
                      </IconButton>
                    </>
                  )}

                  {trashMode && (
                    <>
                      <IconButton
                        title="Restore thread"
                        onClick={async () => {
                          try {
                            await fetchWithAuth(`${API_BASE}/messages/thread/${thread.thread_id}/restore`, { method: "POST", headers });
                            await loadThreads(page, pageSize, trashMode, { silent: true });
                          } catch {
                            showError("Could not restore thread");
                          }
                        }}
                        className="text-green-700 dark:text-green-300 hover:bg-green-50"
                      >
                        <RestoreIcon />
                      </IconButton>

                      <IconButton
                        title="Delete permanently"
                        onClick={() => setPermanentDeleteTarget({ kind: "thread", id: thread.thread_id })}
                        className="text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/40"
                      >
                        <Icon name="trash" className="text-base leading-none" />
                      </IconButton>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Inbox list items: work-order view keeps its status sections; the standard inbox groups a car
  // request's fan-out threads into a bubble; trash stays flat.
  const inboxRenderItems = groupedWorkOrderThreads
    ? [
        ["Pending", groupedWorkOrderThreads.pending],
        ["Active", groupedWorkOrderThreads.active],
        ["Closed / Rejected", groupedWorkOrderThreads.closed],
      ].flatMap(([label, items]) => [
        ...(items.length ? [{ type: "wo_header", label }] : []),
        ...items.map((t) => ({ type: "thread", thread: t })),
      ])
    : trashMode
    ? filteredThreads.map((t) => ({ type: "thread", thread: t }))
    : groupThreadsForInbox(filteredThreads);

  return (
    <div className="p-4">
      <style>{`@keyframes gc-refresh-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      {permanentDeleteDialog}
      <div className="flex items-center gap-2 mb-3">
        <h2 className="text-xl font-bold">{messagingSections.find((s) => s.key === activeSection)?.label || "Inbox"}</h2>
      </div>

      <PushPrompt token={token} prefEnabled={prefEnabled} userId={currentUserId} />

      {/* Section navigation: segmented control on desktop, dropdown on mobile */}
      <div className="mb-3 flex items-center gap-2 rounded-xl border border-blue-300 dark:border-blue-800 bg-blue-50/40 dark:bg-blue-950/40 p-2">
        <div className="hidden sm:inline-flex rounded-xl bg-gray-100 dark:bg-gray-900 p-1">
          {messagingSections.map((s) => {
            const isActive = activeSection === s.key;
            return (
              <button
                key={s.key}
                type="button"
                onClick={() => selectSection(s.key)}
                aria-pressed={isActive}
                className={
                  "inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition " +
                  (isActive ? "bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm" : "text-gray-600 dark:text-gray-300 hover:text-gray-900")
                }
              >
                {s.label}
                {s.count > 0 ? (
                  <span
                    className={
                      "inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-xs font-semibold " +
                      (isActive ? "bg-blue-600 text-white" : "bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-200")
                    }
                  >
                    {s.count}
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>

        <select
          value={activeSection}
          onChange={(e) => selectSection(e.target.value)}
          className="sm:hidden flex-1 border border-gray-300 dark:border-gray-600 rounded-xl px-3 py-2"
          aria-label="Messaging section"
        >
          {messagingSections.map((s) => (
            <option key={s.key} value={s.key}>
              {s.label}
              {s.count > 0 ? ` (${s.count})` : ""}
            </option>
          ))}
        </select>

        <RefreshButton onClick={refreshNow} isRefreshing={isRefreshing} />
      </div>

      {/* Search, filters, page size */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <input
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search threads…"
          className="flex-1 min-w-[220px] border border-gray-300 dark:border-gray-600 rounded px-3 py-2"
        />

        <div className="flex items-center gap-2">
          {/* These filter server-side, so reset to page 1 - the result set changes size. */}
          <FilterChip
            active={unreadOnly}
            disabled={trashMode}
            onToggle={() => {
              setPage(1);
              setUnreadOnly(!unreadOnly);
            }}
          >
            Unread
          </FilterChip>
          <FilterChip
            active={starredOnly}
            disabled={trashMode}
            onToggle={() => {
              setPage(1);
              setStarredOnly(!starredOnly);
            }}
          >
            Starred
          </FilterChip>
        </div>

        <span className="ml-auto">
          Threads per page:&nbsp;
          <select value={pageSize} onChange={handlePageSizeChange} className="border px-2 py-1 rounded">
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
          </select>
        </span>
      </div>

      <div className="mb-3 flex flex-wrap gap-2 items-center">
        {trashMode ? (
          <>
            <button
              onClick={restoreSelected}
              disabled={selectedIds.length === 0}
              className="bg-green-500 text-white w-10 h-10 rounded-xl inline-flex items-center justify-center hover:brightness-95 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
              title="Restore Selected"
              aria-label="Restore Selected"
            >
              <Icon name="time-past" variant="solidRounded" className="text-white text-lg leading-none" />
            </button>
            <button
              onClick={() => setShowPermanentDeleteSelectedConfirm(true)}
              disabled={selectedIds.length === 0}
              className="bg-red-700 text-white w-10 h-10 rounded-xl inline-flex items-center justify-center hover:brightness-95 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
              title="Delete Selected Permanently"
              aria-label="Delete Selected Permanently"
            >
              <Icon name="trash-xmark" className="text-white text-lg leading-none" />
            </button>
            <button
              onClick={() => setShowEmptyTrashConfirm(true)}
              className="bg-red-900 text-white w-10 h-10 rounded-xl inline-flex items-center justify-center hover:brightness-95 active:scale-[0.98]"
              title="Empty Trash"
              aria-label="Empty Trash"
            >
              <Icon name="trash-slash" className="text-white text-lg leading-none" />
            </button>
          </>
        ) : (
          <>
            <button
              onClick={() => setShowTrashSelectedConfirm(true)}
              disabled={selectedIds.length === 0}
              className="bg-red-500 text-white w-10 h-10 rounded-xl inline-flex items-center justify-center hover:brightness-95 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
              title="Trash Selected"
              aria-label="Trash Selected"
            >
              <Icon name="trash" className="text-white text-lg leading-none" />
            </button>
            <button
              onClick={markSelectedUnread}
              disabled={selectedIds.length === 0}
              className="bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-900 dark:text-gray-100 w-10 h-10 rounded-xl inline-flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
              title="Mark Unread Selected"
              aria-label="Mark Unread Selected"
            >
              <Icon name="envelope-dot" variant="solidRounded" className="text-lg leading-none" />
            </button>
            <button
              onClick={markSelectedRead}
              disabled={selectedIds.length === 0}
              className="bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-900 dark:text-gray-100 w-10 h-10 rounded-xl inline-flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
              title="Mark Read Selected"
              aria-label="Mark Read Selected"
            >
              <Icon name="envelope" variant="regularRounded" className="text-lg leading-none" />
            </button>
          </>
        )}
      </div>

      <div className="mb-3 flex gap-2 items-center">
        <button disabled={page === 1} onClick={() => handlePageChange(false)} className="px-2 py-1 border rounded-xl bg-gray-200 dark:bg-gray-700">
          Prev
        </button>
        <span>Page {page}</span>
        <button disabled={!hasNextPage} onClick={() => handlePageChange(true)} className="px-2 py-1 border rounded-xl bg-gray-200 dark:bg-gray-700">
          Next
        </button>

        <span className="ml-auto text-sm text-gray-600 dark:text-gray-300">
          Showing {filteredThreads.length} thread{filteredThreads.length === 1 ? "" : "s"}
          {/* Section + unread/starred are filtered server-side; only search is page-local. */}
          {searchQuery.trim() ? " (search matches on this page only)" : ""}
        </span>
      </div>

      {error && <p className="text-red-600 dark:text-red-400 mb-2">{error}</p>}
      {loadingThreads && filteredThreads.length === 0 && !error && (
        <p className="text-gray-500 dark:text-gray-400">Loading messages...</p>
      )}
      {!loadingThreads && filteredThreads.length === 0 && !error && (
        searchQuery.trim() || (!trashMode && (unreadOnly || starredOnly)) ? (
          <div className="text-sm text-gray-600 dark:text-gray-300">
            <p>No threads match your filters.</p>
            <button
              type="button"
              onClick={() => {
                setPage(1);
                setSearchQuery("");
                setUnreadOnly(false);
                setStarredOnly(false);
              }}
              className="mt-1 text-blue-700 dark:text-blue-300 hover:underline"
            >
              Clear filters
            </button>
          </div>
        ) : trashMode ? (
          <p className="text-sm text-gray-600 dark:text-gray-300">Trash is empty.</p>
        ) : (
          <p className="text-sm text-gray-600 dark:text-gray-300">
            No messages yet. Conversations with shops and car owners will show up here.
          </p>
        )
      )}

      <ul className="space-y-2">
        {inboxRenderItems.map((item) => {
          if (item.type === "wo_header") {
            return (
              <li key={`group-${item.label}`} className="pt-2">
                <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">{item.label}</div>
              </li>
            );
          }
          if (item.type === "group") {
            const vehicle = formatRequestSummary(
              (item.threads.find((t) => t.pinned_summary) || {}).pinned_summary || ""
            );
            const shopCount = item.threads[0].request_group_size || item.threads.length;
            return (
              <li key={`grp-${item.groupId}`}>
                <div className="rounded-2xl border border-blue-300 dark:border-blue-800 bg-blue-50/40 dark:bg-blue-950/20 p-2 sm:p-3">
                  <div className="px-1 pb-2 text-xs font-semibold text-blue-900 dark:text-blue-200">
                    Your request{vehicle ? ` - ${vehicle}` : ""} - sent to {shopCount} shops
                  </div>
                  <div className="space-y-2">
                    {item.threads.map((t) => renderThreadCard(t))}
                  </div>
                </div>
              </li>
            );
          }
          return <li key={item.thread.thread_id}>{renderThreadCard(item.thread)}</li>;
        })}
      </ul>

      {showTrashSelectedConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" role="dialog" aria-modal="true">
          <div className="w-full max-w-sm rounded-2xl bg-white dark:bg-gray-800 p-5 shadow-xl">
            <p className="text-base font-semibold text-gray-900 dark:text-gray-100">Delete the selected threads?</p>
            <div className="mt-4 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={confirmTrashSelected}
                className="bg-green-500 text-white px-4 py-2 rounded-xl inline-flex items-center justify-center gap-2 hover:brightness-95 active:scale-[0.98]"
              >
                <i className="fi fi-rr-check not-italic" />
                Yes
              </button>
              <button
                type="button"
                onClick={() => setShowTrashSelectedConfirm(false)}
                className="bg-red-500 text-white px-4 py-2 rounded-xl inline-flex items-center justify-center gap-2 hover:brightness-95 active:scale-[0.98]"
              >
                <i className="fi fi-rr-cross not-italic" />
                No
              </button>
            </div>
          </div>
        </div>
      )}

      {showMarkAllReadConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" role="dialog" aria-modal="true">
          <div className="w-full max-w-sm rounded-2xl bg-white dark:bg-gray-800 p-5 shadow-xl">
            <p className="text-base font-semibold text-gray-900 dark:text-gray-100">Mark all messages as read?</p>
            <div className="mt-4 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={markAllAsRead}
                className="bg-green-500 text-white px-4 py-2 rounded-xl inline-flex items-center justify-center gap-2 hover:brightness-95 active:scale-[0.98]"
              >
                <i className="fi fi-rr-check not-italic" />
                Yes
              </button>
              <button
                type="button"
                onClick={() => setShowMarkAllReadConfirm(false)}
                className="bg-red-500 text-white px-4 py-2 rounded-xl inline-flex items-center justify-center gap-2 hover:brightness-95 active:scale-[0.98]"
              >
                <i className="fi fi-rr-cross not-italic" />
                No
              </button>
            </div>
          </div>
        </div>
      )}

      {showPermanentDeleteSelectedConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" role="dialog" aria-modal="true">
          <div className="w-full max-w-sm rounded-2xl bg-white dark:bg-gray-800 p-5 shadow-xl">
            <p className="text-base font-semibold text-gray-900 dark:text-gray-100">Permanently delete the selected threads?</p>
            <div className="mt-4 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={permanentlyDeleteSelected}
                className="bg-green-500 text-white px-4 py-2 rounded-xl inline-flex items-center justify-center gap-2 hover:brightness-95 active:scale-[0.98]"
              >
                <i className="fi fi-rr-check not-italic" />
                Yes
              </button>
              <button
                type="button"
                onClick={() => setShowPermanentDeleteSelectedConfirm(false)}
                className="bg-red-500 text-white px-4 py-2 rounded-xl inline-flex items-center justify-center gap-2 hover:brightness-95 active:scale-[0.98]"
              >
                <i className="fi fi-rr-cross not-italic" />
                No
              </button>
            </div>
          </div>
        </div>
      )}

      {showEmptyTrashConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" role="dialog" aria-modal="true">
          <div className="w-full max-w-sm rounded-2xl bg-white dark:bg-gray-800 p-5 shadow-xl">
            <p className="text-base font-semibold text-gray-900 dark:text-gray-100">Permanently delete all threads in trash?</p>
            <div className="mt-4 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={emptyTrash}
                className="bg-green-500 text-white px-4 py-2 rounded-xl inline-flex items-center justify-center gap-2 hover:brightness-95 active:scale-[0.98]"
              >
                <i className="fi fi-rr-check not-italic" />
                Yes
              </button>
              <button
                type="button"
                onClick={() => setShowEmptyTrashConfirm(false)}
                className="bg-red-500 text-white px-4 py-2 rounded-xl inline-flex items-center justify-center gap-2 hover:brightness-95 active:scale-[0.98]"
              >
                <i className="fi fi-rr-cross not-italic" />
                No
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MessageInbox;
